import React, { useState, useEffect, useRef } from "react";
import { createClient } from '@supabase/supabase-js';
const SUPABASE_URL = "https://ieqdgcxqhbbkqqispbtj.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImllcWRnY3hxaGJia3FxaXNwYnRqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY4MDM5MDksImV4cCI6MjA5MjM3OTkwOX0.2A24zW9utbnwJBloJmUdwsla_WT-Vj54d_oLw375VdQ";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── Palette ────────────────────────────────────────────────────────────────────
const SF="#4A9E8E",SFD="#2D7060",SFB="#E8F7F5";
const R="#2D4A3E",RD="#1E3229",RB="#EBF4EE";
const AM="#C49060",AB="#F7EDE0",AD="#7A4A10";
const DK="#1E2E28",MD="#4A6558",LT="#8AAA98";
const BD="rgba(30,70,50,0.10)",BG="#F5F0E8";
const ERR="#E53935",ERRB="#FFEBEE";
const ADMIN_EMAILS=["aldeara@proton.me","rowanarya91@proton.me"];

const WEATHER_API_KEY="15ce3bdfddb5a0e2d36b4070cf6cf07e";

const WRAP={fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",background:"#E8E3D8",minHeight:"100vh",display:"flex",justifyContent:"center"};
const SHELL={width:"100%",maxWidth:430,background:BG,minHeight:"100vh",display:"flex",flexDirection:"column",position:"relative",overflow:"hidden"};

// ── Seed data ──────────────────────────────────────────────────────────────────
const FAMILIES={
  f1:{id:"f1",name:"Johnson Family",emoji:"👨‍👩‍👧",bg:"#EDE9FF",parents:[{name:"Emily Johnson",role:"Mom",phone:"617-555-0143"},{name:"David Johnson",role:"Dad",phone:"617-555-0157"}],address:"14 Marlborough St, Boston MA 02116",isNannyShare:false,caregiverBirthday:"1990-06-12",anniversary:"2015-09-20"},
  f2:{id:"f2",name:"Rivera Family",emoji:"👩‍👧‍👦",bg:"#FFF3E0",parents:[{name:"Sofia Rivera",role:"Mom",phone:"617-555-0198"}],address:"22 Beacon St, Boston MA 02108",isNannyShare:true,caregiverBirthday:"1990-06-12",anniversary:"2018-07-04"},
};
const CAREGIVER={id:"c1",name:"Marcus T.",firstName:"Marcus",emoji:"👨",bg:"#EDE9FF",phone:"617-555-0198",birthday:"1990-06-12",certifications:[
  {id:"cert1",name:"CPR & First Aid",issuer:"Red Cross",expiry:"2026-08-15"},
  {id:"cert2",name:"Early Childhood Education",issuer:"Lesley University",expiry:"2027-03-01"},
]};
const CHILDREN={
  f1:[{id:"ch1",name:"KC",dob:"2024-10-29",emoji:"🧒",bg:"#EBF4EE",allergies:[],photo:null}],
  f2:[
    {id:"ch2",name:"Marco Rivera",dob:"2023-03-12",emoji:"🧒",bg:"#FFE4CC",allergies:[],photo:null},
    {id:"ch3",name:"Sofia Rivera",dob:"2021-08-05",emoji:"🧒",bg:"#F0D6F5",allergies:[],photo:null},
  ],
};

const TODAY=new Date();
const fmtDate=(d)=>new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
const ageMonths=(dob)=>Math.floor((TODAY-new Date(dob))/(1000*60*60*24*30.44));
const ageStr=(dob)=>{const m=ageMonths(dob);return m<24?`${m}mo`:`${Math.floor(m/12)}yr ${m%12}mo`;};
const daysUntil=(dateStr)=>{
  const d=new Date(dateStr);
  const now=new Date();
  // next occurrence
  const next=new Date(now.getFullYear(),d.getMonth(),d.getDate());
  if(next<now)next.setFullYear(now.getFullYear()+1);
  return Math.ceil((next-now)/(1000*60*60*24));
};

// ── Smart sleep algorithm ──────────────────────────────────────────────────────
const BASE_WAKE_WINDOWS={
  // Source: Taking Cara Babies — Cara Dumaplin RN
  0:  [[35,60]],
  4:  [[60,90]],
  12: [[75,120]],
  20: [[120,180]],
  28: [[150,210]],
  44: [[180,240]],
  56: [[240,360]],
};
const getBaseWindow=(ageM)=>{
  const keys=Object.keys(BASE_WAKE_WINDOWS).map(Number).sort((a,b)=>a-b);
  let bracket=keys[0];
  for(const k of keys)if(ageM>=k)bracket=k;
  return BASE_WAKE_WINDOWS[bracket][0];
};
const calcSweetSpot=(child,sleepHistory,nightLog,dataDays)=>{
  const ageM=ageMonths(child.dob);
  const[minW,maxW]=getBaseWindow(ageM);
  const lastSleep=sleepHistory&&sleepHistory.length>0?sleepHistory[0]:null;
  let qualMult=1;
  if(lastSleep){
    if(lastSleep.quality==="great") qualMult=1.10;
    else if(lastSleep.quality==="fair") qualMult=0.90;
    else if(lastSleep.quality==="poor") qualMult=0.80;
  }
  let contMult=1;
  if(lastSleep){
    const wakes=lastSleep.wakes||0;
    if(wakes===1) contMult=0.93;
    else if(wakes===2) contMult=0.87;
    else if(wakes>=3) contMult=0.80;
  }
  let nightAdj=0;
  if(nightLog){
    if(nightLog.duration<480) nightAdj=-20;
    else if(nightLog.duration<540) nightAdj=-10;
    else if(nightLog.duration>660) nightAdj+=10;
    if(nightLog.wakes>=3) nightAdj-=15;
    else if(nightLog.wakes===0) nightAdj+=5;
  }
  const modMin=Math.round(minW*qualMult*contMult)+nightAdj;
  const modMax=Math.round(maxW*qualMult*contMult)+nightAdj;
  const w=Math.min((dataDays||0)/14,1);
  return[
    Math.max(30,Math.round(minW*(1-w)+modMin*w)),
    Math.max(45,Math.round(maxW*(1-w)+modMax*w))
  ];
};

const calcConfidence=(days)=>{
  if(days<3) return{label:"Learning",color:"#C49060"};
  if(days<8) return{label:"Getting there",color:"#C49060"};
  if(days<15) return{label:"Understanding",color:"#2D7060"};
  return{label:"Confident",color:"#2D4A3E"};
};

// ── Seed data ──────────────────────────────────────────────────────────────────
const SEED_TRACKING={
  ch1:{sleepHistory:[],bottles:[],meals:[],diapers:[],toileting:[],tummyTime:[],adventures:[],nursing:[]},
};

// Night sleep logs (family-side) — keyed by childId then date
const SEED_NIGHT_SLEEP={ch1:{}};

const SEED_INCIDENTS=[
  {id:"i1",date:"2026-04-18",time:"14:30",childId:"ch1",childName:"Lily",location:"Living room",details:"Lily tripped on the rug and bumped her forehead on the coffee table corner.",steps:"Applied ice pack for 10 minutes. No swelling or disorientation. Parents called immediately.",calledParents:true,createdBy:"caregiver"},
];
const SEED_MILEAGE=[
  {id:"ml1",date:"2026-04-18",start:"Home",end:"Beacon Hill Playground",miles:2.4,purpose:"Playground trip",family:"f1"},
  {id:"ml2",date:"2026-04-17",start:"Home",end:"Boston Public Library",miles:1.8,purpose:"Story time",family:"f1"},
];
const SEED_MSGS=[
  {id:"msg1",from:"family",text:"Good morning Marcus! Lily had a rough night so she might be tired today.",time:"7:44 AM",date:"Today",type:"text"},
  {id:"msg2",from:"caregiver",text:"Got it, we'll keep things calm. Quiet reading morning.",time:"7:51 AM",date:"Today",type:"text"},
  {id:"msg3",from:"caregiver",text:"📸 Photo shared",time:"10:30 AM",date:"Today",type:"photo",emoji:"🌸"},
  {id:"msg4",from:"caregiver",text:"End of day: Lily perked up after lunch. Oliver had a great day. 🎨",time:"5:03 PM",date:"Today",type:"text"},
];
const SEED_TODOS=[
  {id:"td1",text:"Dishwasher — unload and reload",done:false,contractual:true},
  {id:"td2",text:"Kids laundry — wash and fold",done:false,contractual:true},
  {id:"td3",text:"Oliver homework",done:true,contractual:false},
  {id:"td4",text:"Restock snack drawer",done:false,contractual:false},
];
const SEED_CALENDAR=[
  {id:"ev1",date:"2026-04-22",title:"Music class — Lily",time:"10:00 AM",type:"class"},
  {id:"ev2",date:"2026-04-23",title:"Pediatrician — Oliver",time:"2:30 PM",type:"appointment"},
  {id:"ev3",date:"2026-04-25",title:"Playdate — Marco & Lily",time:"11:00 AM",type:"playdate"},
  {id:"ev4",date:"2026-04-28",title:"Emily's birthday 🎂",time:"",type:"birthday"},
];
const SEED_EMERGENCY=[
  {id:"em1",category:"Medical",contacts:[
    {name:"Poison Control",phone:"1-800-222-1222",note:"Available 24/7"},
    {name:"Dr. Patel (Pediatrician)",phone:"617-555-0220",note:"Call first for non-emergency"},
    {name:"Dr. Kim (Pediatric Dentist)",phone:"617-555-0240",note:""},
    {name:"Children's Hospital Boston",phone:"617-355-6000",note:"Emergency dept"},
  ]},
  {id:"em2",category:"Family",contacts:[
    {name:"Sarah Johnson (Grandmother)",phone:"617-555-0182",note:"First call if parents unreachable"},
    {name:"Uncle Tom",phone:"617-555-0165",note:""},
  ]},
  {id:"em3",category:"School & Classes",contacts:[
    {name:"Beacon Hill Elementary",phone:"617-555-0300",note:"Oliver's school — Ms. Rivera"},
    {name:"Little Stars Preschool",phone:"617-555-0315",note:"Lily's preschool"},
    {name:"Music Together",phone:"617-555-0390",note:"Monday 10am class"},
  ]},
  {id:"em4",category:"Favourite Playmates",contacts:[
    {name:"Marco's mom — Sofia Rivera",phone:"617-555-0198",note:""},
    {name:"Jake's dad — Tom Chen",phone:"617-555-0277",note:""},
  ]},
];
const SEED_ABSENCES=[
  {id:"ab1",date:"2026-04-07",type:"sick",notes:"Stomach bug — called in at 7am",family:"f1"},
  {id:"ab2",date:"2026-04-14",type:"holiday",notes:"Patriot's Day",family:"f1"},
];
const ADVENTURE_OPTIONS=["Playground","Library","Museum","State park","Pool","Class","Playdate","Beach","Farm","Nature walk","Other"];
const QUICK_REPLIES=["On my way!","Running 5 min late","Everything's great 👍","Quick question when you have a sec","Heading out for a walk","End of day update:","Kids are napping 😴"];

// ── Helpers ────────────────────────────────────────────────────────────────────
function Chip({t,bg=SFB,tc=SFD,size=11}){return<span style={{fontSize:size,padding:"3px 9px",borderRadius:20,background:bg,color:tc,fontWeight:600,display:"inline-block",flexShrink:0}}>{t}</span>;}
function Avatar({p,size=44,fontSize=22}){return<div style={{width:size,height:size,borderRadius:"50%",background:p.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize,flexShrink:0}}>{p.emoji}</div>;}
function SectionHeader({title,action,onAction}){return<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}><div style={{fontSize:12,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.05em"}}>{title}</div>{action&&<button onClick={onAction} style={{padding:"4px 12px",borderRadius:20,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:12,fontWeight:600}}>{action}</button>}</div>;}
function Input({label,children,style={}}){return<div style={{marginBottom:10,...style}}>{label&&<label style={{fontSize:11,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.04em",display:"block",marginBottom:4}}>{label}</label>}{children}</div>;}
// inputSt moved inside component for theme access

function ChatInput({onSend,onPhoto}){
  const[draft,setDraft]=useState("");
  const[showQ,setShowQ]=useState(false);
  const send=()=>{if(!draft.trim())return;onSend(draft.trim());setDraft("");setShowQ(false);};
  return(
    <div style={{borderTop:`0.5px solid ${BD}`,background:"#fff",flexShrink:0}}>
      {showQ&&<div style={{display:"flex",gap:6,padding:"8px 12px",overflowX:"auto",borderBottom:`0.5px solid ${BD}`}}>
        {QUICK_REPLIES.map(q=><button key={q} onClick={()=>{setDraft(q);setShowQ(false);}} style={{flexShrink:0,padding:"5px 12px",borderRadius:20,border:"1px solid "+BD,background:"#fff",fontSize:12,color:MD,cursor:"pointer",whiteSpace:"nowrap"}}>{q}</button>)}
      </div>}
      <div style={{display:"flex",gap:8,padding:"10px 12px",alignItems:"center"}}>
        <button onClick={()=>setShowQ(s=>!s)} style={{width:34,height:34,borderRadius:"50%",border:"1px solid "+BD,background:showQ?SFB:"#fff",fontSize:16,cursor:"pointer",flexShrink:0,color:SF}}>⚡</button>
        <button onClick={onPhoto} style={{width:34,height:34,borderRadius:"50%",border:"1px solid "+BD,background:"#fff",fontSize:16,cursor:"pointer",flexShrink:0,color:SF}}>📷</button>
        <input value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} placeholder="Message…" style={{flex:1,padding:"9px 14px",borderRadius:22,border:"1px solid "+BD,fontSize:13,outline:"none",background:"#fff"}}/>
        <button onClick={send} disabled={!draft.trim()} style={{padding:"9px 16px",borderRadius:22,background:draft.trim()?SF:"#D4CFC4",color:draft.trim()?"#fff":LT,border:"none",cursor:draft.trim()?"pointer":"default",fontSize:13,fontWeight:700,flexShrink:0}}>Send</button>
      </div>
    </div>
  );
}

// ── Signature canvas ───────────────────────────────────────────────────────────
function SignatureCanvas({onSave,onCancel}){
  const canvasRef=useRef(null);
  const drawing=useRef(false);
  const getPos=(e,canvas)=>{
    const r=canvas.getBoundingClientRect();
    const src=e.touches?e.touches[0]:e;
    return{x:src.clientX-r.left,y:src.clientY-r.top};
  };
  const start=(e)=>{
    e.preventDefault();
    drawing.current=true;
    const c=canvasRef.current;
    const ctx=c.getContext("2d");
    const{x,y}=getPos(e,c);
    ctx.beginPath();ctx.moveTo(x,y);
  };
  const move=(e)=>{
    e.preventDefault();
    if(!drawing.current)return;
    const c=canvasRef.current;
    const ctx=c.getContext("2d");
    const{x,y}=getPos(e,c);
    ctx.lineWidth=2;ctx.lineCap="round";ctx.strokeStyle=DK;
    ctx.lineTo(x,y);ctx.stroke();
  };
  const end=()=>{drawing.current=false;};
  const clear=()=>{const c=canvasRef.current;c.getContext("2d").clearRect(0,0,c.width,c.height);};
  const save=()=>{
    const c=canvasRef.current;
    const dataUrl=c.toDataURL("image/png");
    onSave(dataUrl);
  };
  return(
    <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:60,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
      <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px"}} onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:4}}>Draw your signature</div>
        <div style={{fontSize:12,color:LT,marginBottom:14}}>Sign with your finger in the box below</div>
        <div style={{border:"1.5px solid "+BD,borderRadius:12,overflow:"hidden",marginBottom:12,background:"#FAFAF8"}}>
          <canvas
            ref={canvasRef}
            width={390}
            height={160}
            style={{display:"block",touchAction:"none",cursor:"crosshair"}}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={clear} style={{padding:"10px 16px",borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Clear</button>
          <button onClick={onCancel} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Cancel</button>
          <button onClick={save} style={{flex:1,padding:12,borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Sign document</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════

// ── Weather activity suggestions ───────────────────────────────────────────────
const WEATHER_ACTIVITIES={
  rainy:{
    young:["Sensory bin — fill a tub with rice or pasta","Finger painting","Dance party to favourite songs","Read together","Peek-a-boo and bubble wrap popping"],
    older:["Indoor obstacle course with pillows","Baking simple cookies","Building a blanket fort","Play-doh or kinetic sand","Storytime followed by drawing"],
  },
  sunny_warm:{
    young:["Bubble chasing in the garden","Sandbox or mud kitchen","Nature walk — collect leaves and sticks","Water table or sprinkler","Chalk on the driveway"],
    older:["Scavenger hunt in the garden","Bike ride or scooter outing","Picnic in the park","Cloud watching","Nature journaling"],
  },
  sunny_cold:{
    young:["Bundled walk to look for birds","Window watching","Sensory bin with kinetic sand","Dancing to upbeat music","Simple puzzles by the window"],
    older:["Snowy walk with magnifying glass","Bird feeder filling","Build a snowman","Hot cocoa making","Nature photography"],
  },
  snowy:{
    young:["Short bundled walk — feel the snow","Watch snowflakes from a warm window","Make snow paint with food colouring","Warm bath after outdoor time","Build a small snowman"],
    older:["Full snow play — snowman, snow angels, fort","Snowflake catching on black paper","Sledding","Snow science — bring snow inside","Hot chocolate bar"],
  },
  cloudy:{
    young:["Tummy time with textured mats","Baby gym exploration","Soft music and gentle movement","Singing and nursery rhymes","Stroller walk"],
    older:["Cloud spotting walk","Puddle jumping in rain boots","Arts and crafts — collage or watercolours","Cooking a simple recipe","Library visit"],
  },
};
export default function AldeaHome(){
  const[role,setRole]=useState("family");
  const[screen,setScreen]=useState("welcome");
  const[user,setUser]=useState(null);
  const[authEmail,setAuthEmail]=useState("");
  const[authPassword,setAuthPassword]=useState("");
  const[authError,setAuthError]=useState("");
  const[authLoading,setAuthLoading]=useState(false);
  const[showConfirmMsg,setShowConfirmMsg]=useState(false);
  const[tab,setTab]=useState("home");
  const[weather,setWeather]=useState({condition:"sunny_warm",temp:62,desc:"Sunny and warm"});
  const[weatherLoading,setWeatherLoading]=useState(false);
  const[selectedChild,setSelectedChild]=useState(null);
  const[sleepHistory,setSleepHistory]=useState({});
  const[dataDays,setDataDays]=useState({});
  const[nightSleepLog,setNightSleepLog]=useState({});
  const[trackingDate,setTrackingDate]=useState(new Date().toISOString().split("T")[0]);
  const[timeOffRequests,setTimeOffRequests]=useState([]);
  const[showTimeOffModal,setShowTimeOffModal]=useState(false);
  const[timeOffForm,setTimeOffForm]=useState({type:"Vacation",date:"",endDate:"",partial:false,departureTime:"",returnTime:"",noReturn:false,hours:"",note:""});
  const[activeFamilyId,setActiveFamilyId]=useState("f1");
  const[activeChildId,setActiveChildId]=useState("ch1");
  const[dark,setDark]=useState(window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches);
  React.useEffect(()=>{
    const mq=window.matchMedia('(prefers-color-scheme: dark)');
    const handler=(e)=>setDark(e.matches);
    mq.addEventListener('change',handler);
    return()=>mq.removeEventListener('change',handler);
  },[]);
  // Theme palette — switches on dark mode
  const T={
    bg:dark?"#0F1A16":BG, card:dark?"#1A2820":"#fff", border:dark?"rgba(100,200,150,0.12)":BD,
    bar:dark?"#111E18":"#fff", text:dark?"#E8F4EE":DK, mid:dark?"#7ABFA0":MD,
    light:dark?"#3A6A52":LT, sfb:dark?"#0D2820":SFB, sfd:dark?"#5DC4A0":SFD,
    input:dark?"#1A2820":"#fff", overlay:dark?"rgba(0,0,0,0.8)":"rgba(20,40,30,0.5)",
  };
  const inputSt={width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+T.border,fontSize:13,outline:"none",boxSizing:"border-box",fontFamily:"inherit",background:T.input,color:T.text};

  const userAuthenticated=user!==null&&user!==undefined;
  const activeFamily=userAuthenticated?{name:"My Family",id:"user"}:FAMILIES[activeFamilyId];
  const children=userAuthenticated?[]:CHILDREN[activeFamilyId]||[];
  const activeChild=children&&children.length>0?children.find(c=>c.id===activeChildId):null||children[0];

  // Timeclock
  const[clockedIn,setClockedIn]=useState(false);
  const[clockInTime,setClockInTime]=useState(null);
  const[elapsed,setElapsed]=useState(0);
  const[clockedInF2,setClockedInF2]=useState(false);
  const[clockInTimeF2,setClockInTimeF2]=useState(null);
  const[elapsedF2,setElapsedF2]=useState(0);
  const[clockLog,setClockLog]=useState([
    {id:"cl1",date:"Mon Apr 14",in:"8:02 AM",out:"5:07 PM",hours:9.08,approved:true,family:"f1"},
    {id:"cl2",date:"Tue Apr 15",in:"7:58 AM",out:"5:01 PM",hours:9.05,approved:true,family:"f1"},
    {id:"cl3",date:"Wed Apr 16",in:"8:05 AM",out:"5:00 PM",hours:8.92,approved:false,family:"f1"},
  ]);
  useEffect(()=>{if(!clockedIn)return;const t=setInterval(()=>setElapsed(s=>s+1),1000);return()=>clearInterval(t);},[clockedIn]);
  useEffect(()=>{if(!clockedInF2)return;const t=setInterval(()=>setElapsedF2(s=>s+1),1000);return()=>clearInterval(t);},[clockedInF2]);
  const fmtElapsed=(s)=>`${Math.floor(s/3600)}:${(Math.floor((s%3600)/60)).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;
  const clockIn=(fid)=>{if(fid==="f1"){setClockedIn(true);setClockInTime(new Date());setElapsed(0);}else{setClockedInF2(true);setClockInTimeF2(new Date());setElapsedF2(0);}};
  const clockOut=(fid)=>{
    const ct=fid==="f1"?clockInTime:clockInTimeF2;
    const el=fid==="f1"?elapsed:elapsedF2;
    if(!ct)return;
    const entry={id:`cl${Date.now()}`,date:new Date().toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}),in:ct.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}),out:new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}),hours:Math.round((el/3600)*100)/100,approved:false,family:fid};
    setClockLog(p=>[entry,...p]);
    if(fid==="f1"){setClockedIn(false);setClockInTime(null);setElapsed(0);}
    else{setClockedInF2(false);setClockInTimeF2(null);setElapsedF2(0);}
  };

  // Tracking
  const[tracking,setTracking]=useState(SEED_TRACKING);
  const[showTrackModal,setShowTrackModal]=useState(null);
  const[activeSleep,setActiveSleep]=useState(null);
  const[adventureOther,setAdventureOther]=useState("");
  const[trackForm,setTrackForm]=useState({logtime:"",bttype:"Breastmilk",btamt:"",btunit:"oz",mlfood:"",mlamt:"",dptype:"wet",ttdur:"",avdest:"Playground",avnote:"",slend:"",slquality:"good",totype:"pee",nrside:"left",nrmanualstart:"",nrmanualend:"",nrnotes:"",nrtimerdur:0});

  // Night sleep (family logs)
  const[nightSleep,setNightSleep]=useState(SEED_NIGHT_SLEEP);
  const[showNightModal,setShowNightModal]=useState(false);
  const[nightForm,setNightForm]=useState({childId:"ch1",bedtime:"",waketime:"",duration:0,wakes:0,quality:"good",notes:""});

  // Sleep
  const startSleep=(childId)=>setActiveSleep({childId,start:new Date()});
  const endSleep=(childId)=>{
    if(!activeSleep||activeSleep.childId!==childId)return;
    const dur=Math.floor((new Date()-activeSleep.start)/60000)||1;
    const entry={id:`sl${Date.now()}`,type:"nap",start:activeSleep.start.toISOString(),end:new Date().toISOString(),duration:dur,quality:"good"};
    setTracking(p=>({...p,[childId]:{...p[childId],sleepHistory:[entry,...(p[childId]?.sleepHistory||[])]}}));
    setActiveSleep(null);
  };
  const getSweetSpot=(child)=>{
    const hist=tracking[child.id]?.sleepHistory||[];
    const now=new Date();
    const todayKey=now.toISOString().split("T")[0];
    const prevDay=new Date(now.getTime()-86400000).toISOString().split("T")[0];
    const nightLog=nightSleep[child.id]?.[todayKey]||nightSleep[child.id]?.[prevDay]||null;
    return calcSweetSpot(child,hist,nightLog,dataDays[child.id]||0);
  };
  // Nursing timer
  const[activeNurse,setActiveNurse]=useState(null);
  const[nurseElapsed,setNurseElapsed]=useState(0);
  useEffect(()=>{if(!activeNurse)return;const t=setInterval(()=>setNurseElapsed(s=>s+1),1000);return()=>clearInterval(t);},[activeNurse]);

  // Incidents
  const[incidents,setIncidents]=useState(SEED_INCIDENTS);
  const[showIncident,setShowIncident]=useState(false);
  const[incidentForm,setIncidentForm]=useState({date:"",time:"",childId:"ch1",location:"",details:"",steps:"",calledParents:false});
  const[incidentStep,setIncidentStep]=useState(1); // 1=details, 2=confirm call

  // Mileage
  const[mileage,setMileage]=useState(SEED_MILEAGE);
  const[showMileage,setShowMileage]=useState(false);
  const[tripActive,setTripActive]=useState(false);
  const[tripStart,setTripStart]=useState("");
  const[tripEnd,setTripEnd]=useState("");
  const[tripMiles,setTripMiles]=useState("");
  const[tripPurpose,setTripPurpose]=useState("");
  const IRS_RATE=0.67;
  const monthMiles=mileage.filter(m=>m.family===activeFamilyId).reduce((a,m)=>a+m.miles,0);

  // Certs
  const[certs,setCerts]=useState(CAREGIVER.certifications);
  const[showCertModal,setShowCertModal]=useState(false);
  const[certForm,setCertForm]=useState({name:"",issuer:"",expiry:""});
  const certStatus=(cert)=>{const d=Math.floor((new Date(cert.expiry)-TODAY)/(1000*60*60*24));if(d<0)return"expired";if(d<=7)return"critical";if(d<=30)return"warning";return"ok";};
  const certColor=(s)=>s==="expired"||s==="critical"?ERR:s==="warning"?AM:SF;

  // Messages
  const[msgs,setMsgs]=useState(SEED_MSGS);
  const chatEnd=useRef(null);
  useEffect(()=>{chatEnd.current?.scrollIntoView({behavior:"smooth"});},[msgs,tab]);
  const handleSendMsg=(text)=>setMsgs(p=>[...p,{id:`msg${Date.now()}`,from:role,text,time:new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}),date:"Today",type:"text"}]);
  const handlePhoto=()=>setMsgs(p=>[...p,{id:`msg${Date.now()}`,from:role,text:"📸 Photo shared",time:new Date().toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}),date:"Today",type:"photo",emoji:"📷"}]);

  // Todos
  const[todos,setTodos]=useState(SEED_TODOS);
  const[newTodo,setNewTodo]=useState("");

  // Calendar
  const[events,setEvents]=useState(SEED_CALENDAR);
  const[showEventModal,setShowEventModal]=useState(false);
  const[eventForm,setEventForm]=useState({date:"",title:"",time:"",type:"class",responsible:""});

  // Documents & signature
  const[docs,setDocs]=useState([{id:"doc1",name:"Work Agreement — Johnson Family",date:"Jan 13 2026",signed:true,sig:null}]);
  const[showSigCanvas,setShowSigCanvas]=useState(false);
  const[sigTargetDoc,setSigTargetDoc]=useState(null);

  // Access codes PIN
  const[pinUnlocked,setPinUnlocked]=useState(false);
  const[pinInput,setPinInput]=useState("");
  const[familyPin,setFamilyPin]=useState("1234");
  const[pinError,setPinError]=useState(false);
  const[showPinChange,setShowPinChange]=useState(false);
  const[newPin,setNewPin]=useState("");
  const accessCodes=[
    {label:"Front door",code:"4521"},{label:"Garage",code:"7890"},
    {label:"Gate",code:"1234"},{label:"Alarm — arm/disarm",code:"9876"},
  ];

  // Reviews
  const[reviews,setReviews]=useState([
    {id:"r1",from:"Emily Johnson",role:"Family",rating:5,text:"Marcus is wonderful with our kids. Reliable, creative, and genuinely caring.",date:"Jan 2026"},
  ]);
  const[showReviewModal,setShowReviewModal]=useState(false);
  const[reviewForm,setReviewForm]=useState({rating:5,text:""});

  // Absences
  const[absences,setAbsences]=useState(SEED_ABSENCES);
  const[showAbsenceModal,setShowAbsenceModal]=useState(false);
  const[absenceForm,setAbsenceForm]=useState({date:"",type:"sick",notes:""});

  const[showMore,setShowMore]=useState(false);

  // ── REMINDERS DATA ─────────────────────────────────────────────────────────
  const reminders=React.useMemo(()=>{
    const list=[];
    const af=activeFamily;
    // NNRW — last week of September (Sep 22 approx)
    list.push({icon:"🌟",text:"National Nanny Recognition Week",sub:"Last week of September",days:daysUntil("2026-09-22"),color:SF,forBoth:true});
    // National Bosses Day — Oct 16
    list.push({icon:"🤝",text:"National Bosses Day",sub:"October 16",days:daysUntil("2026-10-16"),color:AM,forCaregiver:true});
    // Caregiver birthday (family sees this)
    if(af.caregiverBirthday)list.push({icon:"🎂",text:`${CAREGIVER.name}'s birthday`,sub:new Date(af.caregiverBirthday+"T12:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric"}),days:daysUntil(af.caregiverBirthday),color:AM,forFamily:true});
    // Family birthday (caregiver sees)
    if(af.parents[0])list.push({icon:"🎂",text:`${af.parents[0].name}'s birthday`,sub:"Apr 28",days:8,color:AM,forCaregiver:true});
    // Anniversary (family anniversary reminder for caregiver)
    if(af.anniversary)list.push({icon:"💍",text:"Johnson Family anniversary",sub:new Date(af.anniversary+"T12:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric"}),days:daysUntil(af.anniversary),color:SF,forCaregiver:true});
    // Check-ins
    list.push({icon:"🤝",text:"3-month relationship check-in",sub:"Due May 15",days:25,color:R,forBoth:true});
    // Contract renewal
    list.push({icon:"📋",text:"Contract renewal",sub:"Due Jul 13",days:84,color:SF,forBoth:true});
    // Payroll (family)
    list.push({icon:"💵",text:"Weekly payroll reminder",sub:"Every Friday",days:4,color:R,forFamily:true});
    // Yearly bonus (family)
    list.push({icon:"🎁",text:"Year-end bonus reminder",sub:"December",days:daysUntil("2026-12-01"),color:AM,forFamily:true});
    return list.filter(r=>{
      if(r.forBoth)return true;
      if(role==="family"&&r.forFamily)return true;
      if(role==="caregiver"&&r.forCaregiver)return true;
      return false;
    }).sort((a,b)=>a.days-b.days);
  },[role,activeFamilyId]);

  // ── TOP BAR ────────────────────────────────────────────────────────────────
  const TopBar=(
    <div style={{padding:"12px 16px 10px",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`0.5px solid ${T.border}`,flexShrink:0,background:T.bar}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <div style={{fontSize:20,fontWeight:800,color:T.text,letterSpacing:"-0.3px"}}>Al<span style={{color:SF}}>dear</span>a</div>
        <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:T.sfb,color:T.sfd,fontWeight:700}}>Home</span>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <button onClick={()=>setDark(d=>!d)} title={dark?"Light mode":"Dark mode"}
          style={{width:32,height:32,borderRadius:"50%",border:"1px solid "+T.border,background:T.card,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>
          {dark?"☀️":"🌙"}
        </button>
        <div style={{display:"flex",gap:2,background:dark?"#1A2820":"#F0EDE6",borderRadius:22,padding:3}}>
          {["family","caregiver"].map(r=>(
            <button key={r} onClick={()=>setRole(r)} style={{padding:"5px 14px",borderRadius:18,border:"none",cursor:"pointer",fontSize:12,fontWeight:600,background:role===r?SF:"transparent",color:role===r?"#fff":T.light,transition:"all .15s"}}>
              {r==="family"?"Family":"Caregiver"}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── FAMILY SELECTOR (caregiver) ────────────────────────────────────────────
  const FamilySelector=role==="caregiver"&&(
    <div style={{padding:"8px 16px",background:T.bar,borderBottom:`0.5px solid ${T.border}`,display:"flex",gap:8,overflowX:"auto"}}>
      {Object.values(FAMILIES).map(f=>(
        <button key={f.id} onClick={()=>{setActiveFamilyId(f.id);setActiveChildId(CHILDREN[f.id]?.[0]?.id||"");}} style={{flexShrink:0,display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:20,border:"1.5px solid "+activeFamilyId===f.id?SF:T.border,background:activeFamilyId===f.id?T.sfb:T.card,cursor:"pointer"}}>
          <span style={{fontSize:16}}>{f.emoji}</span>
          <span style={{fontSize:12,fontWeight:600,color:activeFamilyId===f.id?T.sfd:T.mid}}>{f.name}</span>
          {activeFamilyId===f.id&&f.isNannyShare&&<Chip t="Share" bg={AB} tc={AD}/>}
        </button>
      ))}
    </div>
  );

  // ── HOME TAB ───────────────────────────────────────────────────────────────
  const HomeTab=(
    <div style={{flex:1,overflowY:"auto",padding:"16px 16px 24px",background:T.bg}}>
      {/* Empty state for new users */}
      {userAuthenticated&&children.length===0&&(
        <div style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,padding:"24px 16px",textAlign:"center",marginBottom:14}}>
          <div style={{fontSize:17,fontWeight:700,color:DK,marginBottom:8}}>Welcome to Aldeara Home</div>
          <div style={{fontSize:13,color:MD,lineHeight:1.7,marginBottom:16}}>Set up your family workspace to get started. Add your children and invite your caregiver.</div>
          <button onClick={()=>alert("Family setup coming in next update!")} style={{padding:"12px 24px",borderRadius:12,background:"linear-gradient(135deg,"+SF+","+SFD+")",color:"#fff",border:"none",cursor:"pointer",fontSize:14,fontWeight:700}}>Set up my family</button>
        </div>
      )}
      {/* Relationship card */}
      <div style={{background:"#fff",borderRadius:16,border:"0.5px solid "+BD,padding:"16px",marginBottom:14,display:"flex",gap:14,alignItems:"center"}}>
        <div style={{position:"relative"}}>
          <Avatar p={role==="family"?CAREGIVER:activeFamily} size={56} fontSize={28}/>
          <div style={{position:"absolute",bottom:-2,right:-2,width:16,height:16,borderRadius:"50%",background:"#22C55E",border:"2px solid #fff"}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:LT,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:2}}>{role==="family"?"Your caregiver":"Your family"}</div>
          <div style={{fontSize:16,fontWeight:700,color:DK}}>{role==="family"?CAREGIVER.name:activeFamily.name}</div>
          <div style={{fontSize:12,color:MD,marginTop:1}}>{role==="family"?"$22/hr · 3 yrs exp":activeFamily.address}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,color:SF,fontWeight:600}}>Active</div>
          <div style={{fontSize:11,color:LT,marginTop:1}}>Week 14</div>
        </div>
      </div>

      {/* Timeclock — caregiver */}
      {role==="caregiver"&&(activeFamily.isNannyShare?(
        <div style={{background:"#fff",borderRadius:16,border:"0.5px solid "+BD,padding:"16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:12}}>Nanny share time clocks</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            {Object.values(FAMILIES).map(f=>{
              const isF1=f.id==="f1";const ci=isF1?clockedIn:clockedInF2;const el=isF1?elapsed:elapsedF2;
              return(
                <div key={f.id} style={{background:SFB,borderRadius:12,padding:"12px",textAlign:"center"}}>
                  <div style={{fontSize:12,fontWeight:600,color:SFD,marginBottom:6}}>{f.name}</div>
                  {ci?(<>
                    <div style={{fontSize:20,fontWeight:800,color:SF,fontVariantNumeric:"tabular-nums",marginBottom:6}}>{fmtElapsed(el)}</div>
                    <button onClick={()=>clockOut(f.id)} style={{width:"100%",padding:8,borderRadius:10,background:R,color:"#fff",border:"none",cursor:"pointer",fontSize:12,fontWeight:700}}>Clock out</button>
                  </>):(
                    <button onClick={()=>clockIn(f.id)} style={{width:"100%",padding:8,borderRadius:10,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:12,fontWeight:700}}>Clock in</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ):(
        <div style={{background:"#fff",borderRadius:16,border:"0.5px solid "+BD,padding:"16px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:12}}>Time clock</div>
          {clockedIn?(
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:36,fontWeight:800,color:SF,fontVariantNumeric:"tabular-nums",letterSpacing:"-1px",marginBottom:4}}>{fmtElapsed(elapsed)}</div>
              <div style={{fontSize:12,color:MD,marginBottom:14}}>Clocked in at {clockInTime?.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</div>
              <button onClick={()=>clockOut("f1")} style={{width:"100%",padding:13,borderRadius:14,background:`linear-gradient(135deg,${R},${RD})`,color:"#fff",border:"none",cursor:"pointer",fontSize:15,fontWeight:700}}>Clock out</button>
            </div>
          ):(
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:14,color:MD,marginBottom:14}}>You're not clocked in yet.</div>
              <button onClick={()=>clockIn("f1")} style={{width:"100%",padding:13,borderRadius:14,background:`linear-gradient(135deg,${SF},${SFD})`,color:"#fff",border:"none",cursor:"pointer",fontSize:15,fontWeight:700}}>Clock in</button>
            </div>
          )}
        </div>
      ))}

      {/* Quick stats */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        {[
          {label:"Hours this week",val:`${Math.round(clockLog.filter(c=>c.family===activeFamilyId).reduce((a,c)=>a+c.hours,0)*10)/10}h`,color:SF},
          {label:"Miles this month",val:`${Math.round(monthMiles*10)/10}mi`,color:AM},
        ].map(({label,val,color})=>(
          <div key={label} style={{background:"#fff",borderRadius:12,border:"0.5px solid "+BD,padding:"14px 12px",textAlign:"center"}}>
            <div style={{fontSize:24,fontWeight:800,color}}>{val}</div>
            <div style={{fontSize:11,color:LT,marginTop:2}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Pending hours approval — family only */}
      {role==="family"&&clockLog.filter(c=>c.family===activeFamilyId&&!c.approved).length>0&&(
        <div style={{background:AB,borderRadius:14,border:"1px solid "+AM,padding:"14px",marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:700,color:AD,marginBottom:10}}>⏳ Hours awaiting your approval</div>
          {clockLog.filter(c=>c.family===activeFamilyId&&!c.approved).map(entry=>(
            <div key={entry.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:DK}}>{entry.date}</div>
                <div style={{fontSize:11,color:MD}}>{entry.in} → {entry.out} · {entry.hours}h</div>
              </div>
              <button onClick={()=>setClockLog(p=>p.map(c=>c.id===entry.id?{...c,approved:true}:c))} style={{padding:"5px 14px",borderRadius:20,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:12,fontWeight:700}}>Approve</button>
            </div>
          ))}
        </div>
      )}

      {/* Weather */}
      {(()=>{
        const acts=WEATHER_ACTIVITIES[weather.condition]?.["young"]||[];
        const icons={rainy:"🌧",snowy:"❄️",sunny_warm:"☀️",sunny_cold:"🌤",cloudy:"☁️"};
        return(
          <div style={{background:T.card,borderRadius:14,border:"0.5px solid "+T.border,padding:"12px 14px",marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:10,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>{icons[weather.condition]} {weather.desc} · {weather.temp}°F</div>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{acts[0]||"Enjoy the day"}</div>
              </div>
              <button onClick={()=>setWeather(p=>{const keys=Object.keys(WEATHER_ACTIVITIES);const next=keys[(keys.indexOf(p.condition)+1)%keys.length];const descs={rainy:"Rainy",sunny_warm:"Sunny & warm",sunny_cold:"Sunny & cold",snowy:"Snowy",cloudy:"Cloudy"};const temps={rainy:48,sunny_warm:72,sunny_cold:38,snowy:28,cloudy:55};return{condition:next,temp:temps[next],desc:descs[next]};})} style={{background:"none",border:"none",cursor:"pointer",fontSize:16,color:T.light}}>↻</button>
            </div>
          </div>
        );
      })()}

      {/* Reminders */}
      <SectionHeader title="Reminders"/>
      <div style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,overflow:"hidden",marginBottom:14}}>
        {reminders.slice(0,5).map(({icon,text,sub,days,color},i,arr)=>(
          <div key={text} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",borderBottom:i<arr.length-1?`0.5px solid ${BD}`:"none"}}>
            <span style={{fontSize:20,flexShrink:0}}>{icon}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:DK}}>{text}</div>
              <div style={{fontSize:11,color:LT}}>{sub}</div>
            </div>
            <span style={{fontSize:11,padding:"2px 8px",borderRadius:20,background:`${color}20`,color,fontWeight:600}}>{days}d</span>
          </div>
        ))}
      </div>
    </div>
  );

  const TrackingTab=(
    <div style={{flex:1,display:"flex",flexDirection:"column",background:T.bg,overflow:"hidden"}}>
      {!selectedChild?(
        <div style={{flex:1,overflowY:"auto",padding:"16px 16px 32px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.06em"}}>{activeFamily?.name}</div>
            {role==="family"&&<button style={{fontSize:12,fontWeight:700,color:SF,background:"none",border:"none",cursor:"pointer",padding:0}}>+ Add child</button>}
          </div>
          {(CHILDREN[activeFamilyId]||[]).length===0&&<div style={{textAlign:"center",padding:"48px 24px",color:T.light,fontSize:13}}>No children added yet.</div>}
          {(CHILDREN[activeFamilyId]||[]).map(child=>(
            <button key={child.id} onClick={()=>setSelectedChild(child)}
              style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"14px 16px",borderRadius:14,border:"0.5px solid "+T.border,background:T.card,cursor:"pointer",marginBottom:10,textAlign:"left"}}>
              <div style={{width:48,height:48,borderRadius:"50%",background:SFB,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,fontWeight:700,color:SF,flexShrink:0}}>
                {child.name?child.name.split(" ").map(p=>p[0]).join("").slice(0,2):"?"}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:15,fontWeight:700,color:T.text}}>{child.name}</div>
                <div style={{fontSize:12,color:T.light,marginTop:1}}>{ageMonths(child.dob)} months old</div>
                {(()=>{try{const hist=sleepHistory[child.id]||[];const sweet=calcSweetSpot(child,hist,null);return sweet?<div style={{fontSize:11,color:SF,fontWeight:600,marginTop:2}}>Nap window: {sweet}</div>:null;}catch(e){return null;}})()}
              {(()=>{try{const days=dataDays[child.id]||0;const conf=calcConfidence(days);return<div style={{fontSize:10,color:conf.color,marginTop:1}}>{conf.label} - {days<1?"first day":days+" days of data"}</div>;}catch(e){return null;}})()}
              </div>
              <span style={{fontSize:20,color:T.light}}>›</span>
            </button>
          ))}
        </div>
      ):(
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{background:T.bar,borderBottom:"0.5px solid "+T.border,flexShrink:0,padding:"10px 16px 0"}}>
            <div style={{display:"flex",alignItems:"center",marginBottom:10}}>
              <button onClick={()=>setSelectedChild(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:13,color:SF,fontWeight:600,padding:0}}>← Children</button>
              <div style={{flex:1,textAlign:"center",fontSize:15,fontWeight:700,color:T.text}}>{selectedChild.name}</div>
              <div style={{width:80}}/>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <button onClick={()=>{const d=new Date(trackingDate);d.setDate(d.getDate()-1);setTrackingDate(d.toISOString().split("T")[0]);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:T.light,padding:"4px 12px"}}>‹</button>
              <div style={{fontSize:14,fontWeight:700,color:T.text}}>{trackingDate===new Date().toISOString().split("T")[0]?"Today":new Date(trackingDate+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</div>
              <button onClick={()=>{const d=new Date(trackingDate);d.setDate(d.getDate()+1);const today=new Date().toISOString().split("T")[0];if(d.toISOString().split("T")[0]<=today)setTrackingDate(d.toISOString().split("T")[0]);}} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:T.light,padding:"4px 12px"}}>›</button>
            </div>
            <button onClick={()=>setShowTrackModal("select")} style={{width:"100%",padding:"13px",borderRadius:12,background:"linear-gradient(135deg,"+SF+","+SFD+")",color:"#fff",border:"none",cursor:"pointer",fontSize:14,fontWeight:700,marginBottom:12}}>Log an entry</button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"12px 16px 32px"}}>
            {(()=>{
              const tr=tracking[selectedChild.id]||{};
              const entries=[
                ...(tr.bottles||[]).map(b=>({id:b.id,time:b.time,text:"Bottle — "+b.amt+b.unit+" "+b.type,tc:SFD})),
                ...(tr.meals||[]).map(m=>({id:m.id,time:m.time,text:"Meal — "+m.food,tc:RD})),
                ...(tr.diapers||[]).map(d=>({id:d.id,time:d.time,text:"Diaper — "+d.type,tc:AD})),
                ...(tr.nursing||[]).map(n=>({id:n.id,time:n.time,text:"Nursing — "+n.side,tc:RD})),
                ...(tr.tummy||[]).map(t=>({id:t.id,time:t.time,text:"Tummy time"+(t.dur?" · "+t.dur+" min":""),tc:SFD})),
                ...(tr.adventures||[]).map(a=>({id:a.id,time:a.time,text:"Adventure — "+a.dest,tc:T})),
                ...(tr.sleeps||[]).map(s=>({id:s.id,time:s.time,text:"Sleep — "+s.start+(s.end?" to "+s.end:""),tc:"#6B5AE0"})),
                ...(tr.medicines||[]).map(m=>({id:m.id,time:m.time,text:"Medicine — "+m.name,tc:ERR,nextDose:m.nextDose})),
                ...(tr.toileting||[]).map(t=>({id:t.id,time:t.time,text:"Toilet — "+t.type,tc:AD})),
              ].sort((a,b)=>b.time>a.time?1:-1);
              if(entries.length===0)return<div style={{textAlign:"center",padding:"32px 16px",color:T.light,fontSize:13}}>No entries yet. Tap Log an entry to start.</div>;
              return entries.map(entry=>(
                <div key={entry.id} style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:8}}>
                  <div style={{width:3,borderRadius:4,background:entry.tc,alignSelf:"stretch",flexShrink:0,minHeight:40}}/>
                  <div style={{flex:1,background:T.card,borderRadius:11,border:"0.5px solid "+T.border,padding:"9px 12px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div style={{fontSize:13,fontWeight:600,color:T.text}}>{entry.text}</div>
                      <div style={{fontSize:11,color:T.light,flexShrink:0,marginLeft:8}}>{entry.time}</div>
                    </div>
                    {entry.nextDose&&<div style={{fontSize:10,color:AD,fontWeight:600,marginTop:2}}>Next dose not before {entry.nextDose}</div>}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
      {showTrackModal==="select"&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={()=>setShowTrackModal(false)}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px"}} onClick={e=>e.stopPropagation()}>
            <div style={{width:40,height:4,background:T.border,borderRadius:2,margin:"0 auto 20px"}}/>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:16,textAlign:"center"}}>Log an entry for {selectedChild?.name}</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[
                {id:"sleep",label:"Sleep"},
                {id:"bottle",label:"Bottle"},
                ...(role==="family"?[{id:"nursing",label:"Nursing"}]:[]),
                {id:"meal",label:"Meal"},
                {id:"diaper",label:"Diaper"},
                {id:"toileting",label:"Toilet"},
                {id:"tummy",label:"Tummy time"},
                {id:"adventure",label:"Adventure"},
                {id:"medicine",label:"Medicine"},
                {id:"incident",label:"Incident"},
              ].map(opt=>(
                <button key={opt.id} onClick={()=>setShowTrackModal(opt.id)}
                  style={{width:"100%",padding:"14px 16px",borderRadius:12,border:"0.5px solid "+T.border,background:T.bg,color:T.text,fontSize:14,fontWeight:600,cursor:"pointer",textAlign:"left"}}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const MessagesTab=(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"10px 14px",display:"flex",alignItems:"center",gap:10,borderBottom:`0.5px solid ${BD}`,flexShrink:0,background:"#fff"}}>
        <Avatar p={role==="family"?CAREGIVER:activeFamily} size={38} fontSize={20}/>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:600,color:DK}}>{role==="family"?CAREGIVER.name:activeFamily.name}</div>
          <div style={{fontSize:11,color:SF}}>Active now · Photos shared here stay private</div>
        </div>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:"14px",display:"flex",flexDirection:"column",gap:8,background:BG}}>
        {msgs.map((m,i)=>{
          const isMe=(role==="family"&&m.from==="family")||(role==="caregiver"&&m.from==="caregiver");
          const showDate=i===0||msgs[i-1].date!==m.date;
          return(
            <div key={m.id}>
              {showDate&&<div style={{textAlign:"center",fontSize:11,color:LT,margin:"4px 0 8px",fontWeight:600}}>{m.date}</div>}
              <div style={{display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",maxWidth:"78%",alignSelf:isMe?"flex-end":"flex-start"}}>
                {m.type==="photo"?(
                  <div style={{background:isMe?SF:"#fff",borderRadius:isMe?"18px 18px 4px 18px":"18px 18px 18px 4px",padding:"16px 20px",border:isMe?"none":`0.5px solid ${BD}`,fontSize:24,textAlign:"center"}}>
                    {m.emoji||"📷"}
                    <div style={{fontSize:11,color:isMe?"rgba(255,255,255,0.8)":LT,marginTop:4}}>Photo shared</div>
                  </div>
                ):(
                  <div style={{background:isMe?SF:"#fff",color:isMe?"#fff":DK,borderRadius:isMe?"18px 18px 4px 18px":"18px 18px 18px 4px",padding:"10px 14px",fontSize:13,lineHeight:1.55,border:isMe?"none":`0.5px solid ${BD}`}}>{m.text}</div>
                )}
                <div style={{fontSize:10,color:LT,marginTop:3}}>{m.time}</div>
              </div>
            </div>
          );
        })}
        <div ref={chatEnd}/>
      </div>
      <ChatInput onSend={handleSendMsg} onPhoto={handlePhoto}/>
    </div>
  );

  // ── INCIDENTS TAB ──────────────────────────────────────────────────────────
  const IncidentsTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <SectionHeader title="Incident & accident reports" action={role==="caregiver"?"+ New report":null} onAction={()=>{setShowIncident(true);setIncidentStep(1);setIncidentForm({date:"",time:"",childId:children[0]?.id||"",location:"",details:"",steps:"",calledParents:false});}}/>
      {incidents.map(inc=>(
        <div key={inc.id} style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,padding:"14px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:DK}}>{inc.childName}</div>
              <div style={{fontSize:12,color:LT}}>{inc.date} · {inc.time} · {inc.location}</div>
            </div>
            {inc.calledParents&&<Chip t="Parents called ✓" bg={RB} tc={R}/>}
          </div>
          <div style={{fontSize:13,color:MD,lineHeight:1.6,marginBottom:6}}><span style={{fontWeight:600,color:DK}}>What happened: </span>{inc.details}</div>
          <div style={{fontSize:13,color:MD,lineHeight:1.6}}><span style={{fontWeight:600,color:DK}}>Steps taken: </span>{inc.steps}</div>
        </div>
      ))}
      {incidents.length===0&&<div style={{textAlign:"center",padding:"40px 0",color:LT,fontSize:13}}>No incidents logged.</div>}

      {showIncident&&(
        <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px",maxHeight:"92vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>

            {incidentStep===1&&(<>
              <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:16}}>New incident report</div>
              <Input label="Child">
                <select value={incidentForm.childId} onChange={e=>setIncidentForm(p=>({...p,childId:e.target.value}))} style={inputSt}>
                  {children.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Input>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <Input label="Date"><input type="date" value={incidentForm.date} onChange={e=>setIncidentForm(p=>({...p,date:e.target.value}))} style={inputSt}/></Input>
                <Input label="Time"><input type="time" value={incidentForm.time} onChange={e=>setIncidentForm(p=>({...p,time:e.target.value}))} style={inputSt}/></Input>
              </div>
              <Input label="Location"><input value={incidentForm.location} onChange={e=>setIncidentForm(p=>({...p,location:e.target.value}))} placeholder="e.g. Living room, backyard, park" style={inputSt}/></Input>
              <Input label="What happened"><textarea value={incidentForm.details} onChange={e=>setIncidentForm(p=>({...p,details:e.target.value}))} rows={3} placeholder="Describe what happened — be specific about how, where and what was injured." style={{...inputSt,resize:"vertical"}}/></Input>
              <Input label="Steps taken"><textarea value={incidentForm.steps} onChange={e=>setIncidentForm(p=>({...p,steps:e.target.value}))} rows={3} placeholder="e.g. Applied ice pack for 10 minutes. Checked for swelling." style={{...inputSt,resize:"vertical"}}/></Input>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setShowIncident(false)} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Cancel</button>
                <button onClick={()=>{
                  if(!incidentForm.details.trim()||!incidentForm.location.trim()){alert("Please fill in all required fields.");return;}
                  setIncidentStep(2);
                }} style={{flex:1,padding:12,borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Continue →</button>
              </div>
            </>)}

            {incidentStep===2&&(<>
              <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:8}}>Before you save</div>
              <div style={{background:ERRB,borderRadius:14,padding:"16px",marginBottom:20,borderLeft:`3px solid ${ERR}`}}>
                <div style={{fontSize:14,fontWeight:700,color:ERR,marginBottom:8}}>📞 Have you called the family?</div>
                <div style={{fontSize:13,color:ERR,lineHeight:1.65}}>The family should hear about this from you directly — not from an app notification. Please call or text them before saving this report.</div>
              </div>
              <div style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,overflow:"hidden",marginBottom:16}}>
                {activeFamily.parents.map((parent,i)=>(
                  <a key={i} href={`tel:${parent.phone}`} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",borderBottom:i<activeFamily.parents.length-1?`0.5px solid ${BD}`:"none",textDecoration:"none"}}>
                    <div>
                      <div style={{fontSize:13,fontWeight:600,color:DK}}>{parent.name}</div>
                      <div style={{fontSize:11,color:LT}}>{parent.role}</div>
                    </div>
                    <span style={{padding:"6px 14px",borderRadius:20,background:RB,color:R,fontSize:12,fontWeight:700}}>📞 {parent.phone}</span>
                  </a>
                ))}
              </div>
              <label style={{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",marginBottom:20,padding:"12px 14px",background:incidentForm.calledParents?RB:"#F9F9F7",borderRadius:12,border:"1px solid "+incidentForm.calledParents?R:BD}}>
                <input type="checkbox" checked={incidentForm.calledParents} onChange={e=>setIncidentForm(p=>({...p,calledParents:e.target.checked}))} style={{width:20,height:20,marginTop:1,accentColor:R,flexShrink:0}}/>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:DK}}>I have contacted the family</div>
                  <div style={{fontSize:12,color:MD,marginTop:1}}>Check this box to confirm you've spoken with or texted the family about this incident.</div>
                </div>
              </label>
              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setIncidentStep(1)} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>← Back</button>
                <button
                  disabled={!incidentForm.calledParents}
                  onClick={()=>{
                    const child=children.find(c=>c.id===incidentForm.childId);
                    setIncidents(p=>[{id:`i${Date.now()}`,...incidentForm,childName:child?.name||"",createdBy:"caregiver"},...p]);
                    setShowIncident(false);setIncidentStep(1);
                  }}
                  style={{flex:1,padding:12,borderRadius:12,background:incidentForm.calledParents?R:"#ccc",color:"#fff",border:"none",cursor:incidentForm.calledParents?"pointer":"not-allowed",fontSize:13,fontWeight:700}}>
                  Save report
                </button>
              </div>
              {!incidentForm.calledParents&&<div style={{textAlign:"center",fontSize:11,color:LT,marginTop:8}}>You must confirm you've contacted the family to save this report.</div>}
            </>)}
          </div>
        </div>
      )}
    </div>
  );

  // ── MILEAGE TAB ────────────────────────────────────────────────────────────
  const MileageTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <div style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,padding:"14px",marginBottom:14}}>
        <div style={{display:"flex",gap:16,marginBottom:14}}>
          <div style={{flex:1,textAlign:"center"}}>
            <div style={{fontSize:24,fontWeight:800,color:AM}}>{Math.round(monthMiles*10)/10}</div>
            <div style={{fontSize:11,color:LT}}>Miles this month</div>
          </div>
          <div style={{flex:1,textAlign:"center"}}>
            <div style={{fontSize:24,fontWeight:800,color:SF}}>${(monthMiles*IRS_RATE).toFixed(2)}</div>
            <div style={{fontSize:11,color:LT}}>Reimbursement due</div>
          </div>
        </div>
        <div style={{fontSize:11,color:LT,textAlign:"center",padding:"8px",background:SFB,borderRadius:8}}>IRS rate: $0.67/mile · Auto-pushed to family at month end</div>
      </div>

      {/* Trip tracker */}
      <div style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,padding:"14px",marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:700,color:DK,marginBottom:12}}>🚗 Trip tracker</div>
        {!tripActive?(
          <div>
            <Input label="Starting point"><input value={tripStart} onChange={e=>setTripStart(e.target.value)} placeholder="e.g. 14 Marlborough St" style={inputSt}/></Input>
            <button onClick={()=>{if(tripStart.trim())setTripActive(true);}} style={{width:"100%",padding:12,borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Start trip</button>
          </div>
        ):(
          <div>
            <div style={{background:SFB,borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:13,color:SFD}}>📍 Trip in progress from <strong>{tripStart}</strong></div>
            <Input label="Destination"><input value={tripEnd} onChange={e=>setTripEnd(e.target.value)} placeholder="e.g. Beacon Hill Playground" style={inputSt}/></Input>
            <Input label="Miles driven"><input type="number" value={tripMiles} onChange={e=>setTripMiles(e.target.value)} placeholder="0.0" style={inputSt}/></Input>
            <Input label="Purpose"><input value={tripPurpose} onChange={e=>setTripPurpose(e.target.value)} placeholder="e.g. Playground trip" style={inputSt}/></Input>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{setTripActive(false);setTripStart("");setTripEnd("");setTripMiles("");setTripPurpose("");}} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>{
                const miles=parseFloat(tripMiles)||0;
                if(!tripEnd.trim()||!miles)return;
                setMileage(p=>[{id:`ml${Date.now()}`,date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),start:tripStart,end:tripEnd,miles,purpose:tripPurpose,family:activeFamilyId},...p]);
                setTripActive(false);setTripStart("");setTripEnd("");setTripMiles("");setTripPurpose("");
              }} style={{flex:1,padding:12,borderRadius:12,background:AM,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>End trip & save</button>
            </div>
          </div>
        )}
      </div>

      <SectionHeader title="Trip log"/>
      {mileage.filter(m=>m.family===activeFamilyId).map(m=>(
        <div key={m.id} style={{background:"#fff",borderRadius:12,border:"0.5px solid "+BD,padding:"12px 14px",marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:DK}}>{m.purpose||m.end}</div>
              <div style={{fontSize:11,color:LT}}>{m.date} · {m.start} → {m.end}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:14,fontWeight:700,color:AM}}>{m.miles}mi</div>
              <div style={{fontSize:11,color:LT}}>${(m.miles*IRS_RATE).toFixed(2)}</div>
            </div>
          </div>
        </div>
      ))}
      {role==="family"&&mileage.filter(m=>m.family===activeFamilyId).length>0&&(
        <div style={{background:AB,borderRadius:10,padding:"10px 14px",fontSize:12,color:AD,marginTop:4}}>💵 Total reimbursement due: <strong>${(monthMiles*IRS_RATE).toFixed(2)}</strong> — include in this week's payroll</div>
      )}
    </div>
  );

  // ── CERTIFICATIONS TAB ─────────────────────────────────────────────────────
  const CertificationsTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <SectionHeader title={role==="caregiver"?"My certifications":"Caregiver certifications"} action={role==="caregiver"?"+ Add":null} onAction={()=>setShowCertModal(true)}/>
      {certs.map(cert=>{
        const s=certStatus(cert);const days=Math.floor((new Date(cert.expiry)-TODAY)/(1000*60*60*24));
        return(
          <div key={cert.id} style={{background:"#fff",borderRadius:14,border:"0.5px solid "+s!=="ok"?certColor(s):BD,padding:"14px",marginBottom:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:700,color:DK}}>{cert.name}</div>
                <div style={{fontSize:12,color:LT}}>{cert.issuer}</div>
              </div>
              <Chip t={s==="ok"?"✓ Current":s==="warning"?"Expires soon":s==="critical"?"Expires this week":"Expired"} bg={s==="ok"?RB:s==="warning"?AB:ERRB} tc={s==="ok"?R:s==="warning"?AD:ERR}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:12,color:s!=="ok"?certColor(s):MD}}>
                {s==="expired"?"Expired":"Expires"} {fmtDate(cert.expiry)} {s!=="ok"&&s!=="expired"?`(${days} days)`:s==="expired"?`(${Math.abs(days)} days ago)`:""}
              </div>
              {role==="caregiver"&&<button onClick={()=>{const newExpiry=prompt("Enter new expiry date (YYYY-MM-DD):");if(newExpiry)setCerts(p=>p.map(c=>c.id===cert.id?{...c,expiry:newExpiry}:c));}} style={{padding:"4px 10px",borderRadius:20,background:SFB,color:SFD,border:"none",cursor:"pointer",fontSize:11,fontWeight:600}}>Update expiry</button>}
            </div>
          </div>
        );
      })}
      {showCertModal&&(
        <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:16}}>Add certification</div>
            <Input label="Certification name"><input value={certForm.name} onChange={e=>setCertForm(p=>({...p,name:e.target.value}))} placeholder="e.g. CPR & First Aid" style={inputSt}/></Input>
            <Input label="Issuing body"><input value={certForm.issuer} onChange={e=>setCertForm(p=>({...p,issuer:e.target.value}))} placeholder="e.g. Red Cross" style={inputSt}/></Input>
            <Input label="Expiry date"><input type="date" value={certForm.expiry} onChange={e=>setCertForm(p=>({...p,expiry:e.target.value}))} style={inputSt}/></Input>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowCertModal(false)} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>{setCerts(p=>[...p,{id:`cert${Date.now()}`,...certForm}]);setCertForm({name:"",issuer:"",expiry:""});setShowCertModal(false);}} style={{flex:1,padding:12,borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── CALENDAR TAB ───────────────────────────────────────────────────────────
  const CalendarTab=(
    <div style={{flex:1,overflowY:"auto",background:T.bg,padding:"16px 16px 32px"}}>
      <SectionHeader title="Upcoming" action="+ Add" onAction={()=>setShowEventModal(true)}/>
      {events.sort((a,b)=>a.date>b.date?1:-1).map(ev=>{
        const typeColor={class:SF,appointment:ERR,playdate:AM,birthday:R}[ev.type]||SF;
        return(
          <div key={ev.id} style={{background:T.card,borderRadius:12,border:"0.5px solid "+T.border,padding:"12px 14px",marginBottom:8,display:"flex",gap:12,alignItems:"center"}}>
            <div style={{width:4,height:40,borderRadius:4,background:typeColor,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:T.text}}>{ev.title}</div>
              <div style={{fontSize:11,color:T.light}}>{new Date(ev.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}{ev.time?` · ${ev.time}`:""}{ev.responsible?` · ${ev.responsible}`:""}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
              <Chip t={ev.type} bg={`${typeColor}15`} tc={typeColor}/>
              <button onClick={()=>setEvents(p=>p.filter(e=>e.id!==ev.id))} style={{background:"none",border:"none",color:T.light,cursor:"pointer",fontSize:12}}>✕</button>
            </div>
          </div>
        );
      })}
      {showEventModal&&(
        <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:16}}>Add event</div>
            <Input label="Event title"><input value={eventForm.title} onChange={e=>setEventForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Music class" style={inputSt}/></Input>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <Input label="Date"><input type="date" value={eventForm.date} onChange={e=>setEventForm(p=>({...p,date:e.target.value}))} style={inputSt}/></Input>
              <Input label="Time (optional)"><input type="time" value={eventForm.time} onChange={e=>setEventForm(p=>({...p,time:e.target.value}))} style={inputSt}/></Input>
            </div>
            <Input label="Type"><select value={eventForm.type} onChange={e=>setEventForm(p=>({...p,type:e.target.value}))} style={inputSt}><option value="class">Class</option><option value="appointment">Appointment</option><option value="playdate">Playdate</option><option value="birthday">Birthday</option><option value="other">Other</option></select></Input>
            <Input label="Responsible adult">
              <select value={eventForm.responsible||""} onChange={e=>setEventForm(p=>({...p,responsible:e.target.value}))} style={inputSt}>
                <option value="">— Select —</option>
                <option value={CAREGIVER.name}>{CAREGIVER.name} (Caregiver)</option>
                {activeFamily.parents.map(p=><option key={p.name} value={p.name}>{p.name} ({p.role})</option>)}
              </select>
            </Input>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowEventModal(false)} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+T.border,background:T.card,color:T.mid,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>{setEvents(p=>[...p,{id:`ev${Date.now()}`,...eventForm}]);setEventForm({date:"",title:"",time:"",type:"class",responsible:""});setShowEventModal(false);}} style={{flex:1,padding:12,borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── TODOS TAB ──────────────────────────────────────────────────────────────
  const TodosTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <SectionHeader title="To-do list"/>
      <div style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,overflow:"hidden",marginBottom:14}}>
        {todos.map((td,i)=>(
          <div key={td.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderBottom:i<todos.length-1?`0.5px solid ${BD}`:"none",background:td.done?"#F9F9F9":"#fff"}}>
            <input type="checkbox" checked={td.done} onChange={()=>setTodos(p=>p.map(t=>t.id===td.id?{...t,done:!t.done}:t))} style={{width:18,height:18,cursor:"pointer",accentColor:SF}}/>
            <div style={{flex:1}}>
              <div style={{fontSize:13,color:td.done?LT:DK,textDecoration:td.done?"line-through":"none",fontWeight:500}}>{td.text}</div>
              {td.contractual&&<div style={{fontSize:10,color:SF,fontWeight:600,marginTop:1}}>In contract</div>}
            </div>
            <button onClick={()=>setTodos(p=>p.filter(t=>t.id!==td.id))} style={{background:"none",border:"none",color:LT,cursor:"pointer",fontSize:14,padding:"2px 6px"}}>✕</button>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:8}}>
        <input value={newTodo} onChange={e=>setNewTodo(e.target.value)} onKeyDown={e=>e.key==="Enter"&&newTodo.trim()&&(setTodos(p=>[...p,{id:`td${Date.now()}`,text:newTodo.trim(),done:false,contractual:false}]),setNewTodo(""))} placeholder="Add a task…" style={{flex:1,padding:"10px 14px",borderRadius:12,border:"1px solid "+BD,fontSize:13,outline:"none",background:"#fff"}}/>
        <button onClick={()=>{if(newTodo.trim()){setTodos(p=>[...p,{id:`td${Date.now()}`,text:newTodo.trim(),done:false,contractual:false}]);setNewTodo("");}}} style={{padding:"10px 18px",borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Add</button>
      </div>
      <div style={{marginTop:10,fontSize:11,color:LT,padding:"8px 12px",background:SFB,borderRadius:8}}>Both family and caregiver can add and check off tasks. Tasks marked "In contract" are agreed in the work agreement.</div>
    </div>
  );

  // ── EMERGENCY TAB ──────────────────────────────────────────────────────────
  const EmergencyTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <div style={{padding:"10px 14px",background:ERRB,borderRadius:12,marginBottom:14,borderLeft:`3px solid ${ERR}`}}>
        <div style={{fontSize:13,fontWeight:700,color:ERR,marginBottom:2}}>Poison Control — always available</div>
        <a href="tel:1-800-222-1222" style={{fontSize:14,fontWeight:800,color:ERR,textDecoration:"none"}}>1-800-222-1222</a>
      </div>
      {SEED_EMERGENCY.map(section=>(
        <div key={section.id} style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,overflow:"hidden",marginBottom:10}}>
          <div style={{padding:"10px 14px",background:SFB,fontSize:12,fontWeight:700,color:SFD,textTransform:"uppercase",letterSpacing:"0.05em"}}>{section.category}</div>
          {section.contacts.map((contact,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 14px",borderBottom:i<section.contacts.length-1?`0.5px solid ${BD}`:"none"}}>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:DK}}>{contact.name}</div>
                {contact.note&&<div style={{fontSize:11,color:LT}}>{contact.note}</div>}
              </div>
              <a href={`tel:${contact.phone}`} style={{padding:"5px 12px",borderRadius:20,background:RB,color:R,textDecoration:"none",fontSize:12,fontWeight:600,flexShrink:0}}>{contact.phone}</a>
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  // ── ACCESS CODES TAB ───────────────────────────────────────────────────────
  const AccessCodesTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <div style={{fontSize:12,color:LT,lineHeight:1.6,marginBottom:14,padding:"10px 14px",background:SFB,borderRadius:10}}>Access codes are protected by a PIN set by the family. The PIN is shared with caregivers separately.</div>
      {!pinUnlocked?(
        <div style={{background:"#fff",borderRadius:16,border:"0.5px solid "+BD,padding:"24px",textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:12}}>🔒</div>
          <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:4}}>Enter PIN to view codes</div>
          <div style={{fontSize:13,color:LT,marginBottom:16}}>(Demo PIN: 1234)</div>
          <input type="password" maxLength={4} value={pinInput} onChange={e=>setPinInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&(pinInput===familyPin?(setPinUnlocked(true),setPinInput(""),setPinError(false)):(setPinError(true),setPinInput("")))} placeholder="• • • •" style={{width:120,padding:"12px",borderRadius:12,border:"2px solid "+pinError?ERR:BD,fontSize:20,textAlign:"center",outline:"none",letterSpacing:"0.3em",marginBottom:10}}/>
          {pinError&&<div style={{fontSize:12,color:ERR,marginBottom:10}}>Incorrect PIN. Try again.</div>}
          <button onClick={()=>{if(pinInput===familyPin){setPinUnlocked(true);setPinInput("");setPinError(false);}else{setPinError(true);setPinInput("");}}} style={{padding:"10px 28px",borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Unlock</button>
          {role==="family"&&<div style={{marginTop:12}}><button onClick={()=>setShowPinChange(true)} style={{padding:"6px 16px",borderRadius:20,background:SFB,color:SFD,border:"none",cursor:"pointer",fontSize:12,fontWeight:600}}>Change PIN</button></div>}
        </div>
      ):(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:12,color:SF,fontWeight:600}}>🔓 Codes visible — tap to copy</div>
            <button onClick={()=>setPinUnlocked(false)} style={{padding:"4px 12px",borderRadius:20,background:SFB,color:SFD,border:"none",cursor:"pointer",fontSize:11,fontWeight:600}}>Lock</button>
          </div>
          {accessCodes.map((code,i)=>(
            <div key={i} onClick={()=>navigator.clipboard?.writeText(code.code)} style={{background:"#fff",borderRadius:12,border:"0.5px solid "+BD,padding:"14px 16px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
              <div style={{fontSize:13,color:MD}}>{code.label}</div>
              <div style={{fontSize:18,fontWeight:800,color:DK,fontVariantNumeric:"tabular-nums",letterSpacing:"0.1em"}}>{code.code}</div>
            </div>
          ))}
          {role==="family"&&<button style={{width:"100%",padding:12,borderRadius:12,background:SFB,color:SFD,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,marginTop:6}}>+ Add access code</button>}
        </div>
      )}
      {showPinChange&&(
        <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px",textAlign:"center"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:16}}>Set new PIN</div>
            <input type="password" maxLength={4} value={newPin} onChange={e=>setNewPin(e.target.value)} placeholder="New 4-digit PIN" style={{width:140,padding:"12px",borderRadius:12,border:"2px solid "+BD,fontSize:20,textAlign:"center",outline:"none",letterSpacing:"0.3em",marginBottom:16}}/>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button onClick={()=>setShowPinChange(false)} style={{padding:"10px 20px",borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>{if(newPin.length===4){setFamilyPin(newPin);setNewPin("");setShowPinChange(false);}}} style={{padding:"10px 20px",borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Save PIN</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── DOCUMENTS TAB ─────────────────────────────────────────────────────────
  const DocumentsTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <SectionHeader title="Documents & contracts" action="+ Upload" onAction={()=>{const name=prompt("Document name:");if(name)setDocs(p=>[...p,{id:`doc${Date.now()}`,name,date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),signed:false,sig:null}]);}}/>
      {docs.map(doc=>(
        <div key={doc.id} style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,padding:"14px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700,color:DK}}>{doc.name}</div>
              <div style={{fontSize:12,color:LT}}>Added {doc.date}</div>
            </div>
            {doc.signed?<Chip t="✓ Signed" bg={RB} tc={R}/>:<Chip t="Unsigned" bg={AB} tc={AD}/>}
          </div>
          {doc.sig&&<img src={doc.sig} alt="signature" style={{border:"1px solid "+BD,borderRadius:8,maxWidth:"100%",height:50,objectFit:"contain",marginBottom:10,background:"#FAFAF8"}}/>}
          <div style={{display:"flex",gap:8}}>
            <button style={{flex:1,padding:9,borderRadius:10,background:SFB,color:SFD,border:"none",cursor:"pointer",fontSize:12,fontWeight:600}}>View PDF</button>
            <button style={{flex:1,padding:9,borderRadius:10,background:RB,color:R,border:"none",cursor:"pointer",fontSize:12,fontWeight:600}}>Download</button>
            {!doc.signed&&<button onClick={()=>{setSigTargetDoc(doc.id);setShowSigCanvas(true);}} style={{flex:1,padding:9,borderRadius:10,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:12,fontWeight:600}}>Sign</button>}
          </div>
        </div>
      ))}
      <div style={{padding:"12px 14px",background:SFB,borderRadius:12,fontSize:12,color:SFD,lineHeight:1.6}}>Both parties receive a copy of all signed documents and can download them at any time.</div>
    </div>
  );

  // ── REVIEWS TAB ───────────────────────────────────────────────────────────
  const ReviewsTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <div style={{padding:"12px 14px",background:AB,borderRadius:12,marginBottom:14,borderLeft:`3px solid ${AM}`}}>
        <div style={{fontSize:13,color:AD,lineHeight:1.6}}>Reviews are visible on Aldeara Match profiles as verified references. They can only be left after a real working relationship.</div>
      </div>
      <SectionHeader title="Reviews" action="+ Leave review" onAction={()=>setShowReviewModal(true)}/>
      {reviews.map(r=>(
        <div key={r.id} style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,padding:"14px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
            <div>
              <div style={{fontSize:14,fontWeight:700,color:DK}}>{r.from}</div>
              <div style={{fontSize:12,color:LT}}>{r.role} · {r.date}</div>
            </div>
            <div style={{fontSize:16}}>{"⭐".repeat(r.rating)}</div>
          </div>
          <div style={{fontSize:13,color:MD,lineHeight:1.6,fontStyle:"italic"}}>"{r.text}"</div>
        </div>
      ))}
      {showReviewModal&&(
        <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:16}}>Leave a review</div>
            <div style={{display:"flex",gap:8,marginBottom:14,justifyContent:"center"}}>
              {[1,2,3,4,5].map(s=>(
                <button key={s} onClick={()=>setReviewForm(p=>({...p,rating:s}))} style={{fontSize:28,background:"none",border:"none",cursor:"pointer",opacity:reviewForm.rating>=s?1:0.3}}>⭐</button>
              ))}
            </div>
            <textarea value={reviewForm.text} onChange={e=>setReviewForm(p=>({...p,text:e.target.value}))} rows={4} placeholder="Share your experience working together…" style={{...inputSt,resize:"vertical",marginBottom:14}}/>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowReviewModal(false)} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>{setReviews(p=>[{id:`r${Date.now()}`,from:role==="family"?"Emily Johnson":"Marcus T.",role:role==="family"?"Family":"Caregiver",rating:reviewForm.rating,text:reviewForm.text,date:new Date().toLocaleDateString("en-US",{month:"short",year:"numeric"})},...p]);setReviewForm({rating:5,text:""});setShowReviewModal(false);}} style={{flex:1,padding:12,borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── ABSENCES TAB ──────────────────────────────────────────────────────────
  const AbsencesTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <SectionHeader title="Absences & leave" action={role==="family"?"+ Log":null} onAction={role==="family"?()=>setShowAbsenceModal(true):null}/>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:14}}>
        {["sick","holiday","weather","bereavement","personal"].map(type=>{
          const count=absences.filter(a=>a.type===type&&a.family===activeFamilyId).length;
          const icons={sick:"🤒",holiday:"🎉",weather:"🌨",bereavement:"🕊",personal:"🌿"};
          const labels={sick:"Sick days",holiday:"Holidays",weather:"Weather",bereavement:"Bereavement",personal:"Personal"};
          return(
            <div key={type} style={{background:"#fff",borderRadius:12,border:"0.5px solid "+BD,padding:"12px 8px",textAlign:"center"}}>
              <div style={{fontSize:20,marginBottom:2}}>{icons[type]}</div>
              <div style={{fontSize:20,fontWeight:800,color:DK}}>{count}</div>
              <div style={{fontSize:10,color:LT,lineHeight:1.3}}>{labels[type]}</div>
            </div>
          );
        })}
      </div>
      {absences.filter(a=>a.family===activeFamilyId).map(ab=>{
        const icons={sick:"🤒",holiday:"🎉",weather:"🌨",bereavement:"🕊",personal:"🌿"};
        return(
          <div key={ab.id} style={{background:"#fff",borderRadius:12,border:"0.5px solid "+BD,padding:"12px 14px",marginBottom:8,display:"flex",gap:12,alignItems:"center"}}>
            <span style={{fontSize:20,flexShrink:0}}>{icons[ab.type]||"📅"}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:DK,textTransform:"capitalize"}}>{ab.type}</div>
              <div style={{fontSize:11,color:LT}}>{ab.date}{ab.notes?` · ${ab.notes}`:""}</div>
            </div>
          </div>
        );
      })}
      {absences.filter(a=>a.family===activeFamilyId).length===0&&<div style={{textAlign:"center",padding:"30px 0",color:LT,fontSize:13}}>No absences logged yet.</div>}

      {showAbsenceModal&&role==="family"&&(
        <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onMouseDown={e=>e.stopPropagation()} onTouchStart={e=>e.stopPropagation()} onTouchEnd={e=>e.stopPropagation()}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:16}}>Log absence</div>
            <Input label="Date"><input type="date" value={absenceForm.date} onChange={e=>setAbsenceForm(p=>({...p,date:e.target.value}))} style={inputSt}/></Input>
            <Input label="Type">
              <select value={absenceForm.type} onChange={e=>setAbsenceForm(p=>({...p,type:e.target.value}))} style={inputSt}>
                <option value="sick">🤒 Sick day</option>
                <option value="holiday">🎉 Holiday</option>
                <option value="weather">🌨 Inclement weather</option>
                <option value="bereavement">🕊 Bereavement</option>
                <option value="personal">🌿 Personal day</option>
              </select>
            </Input>
            <Input label="Notes (optional)"><input value={absenceForm.notes} onChange={e=>setAbsenceForm(p=>({...p,notes:e.target.value}))} placeholder="e.g. Called in at 7am" style={inputSt}/></Input>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setShowAbsenceModal(false)} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+BD,background:"#fff",color:MD,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>{setAbsences(p=>[...p,{id:`ab${Date.now()}`,...absenceForm,family:activeFamilyId,approved:false}]);setAbsenceForm({date:"",type:"sick",notes:""});setShowAbsenceModal(false);}} style={{flex:1,padding:12,borderRadius:12,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const pendingTimeOff=timeOffRequests.filter(r=>r.family===activeFamilyId&&r.status==="pending");
  const TimeOffTab=(
    <div style={{flex:1,overflowY:"auto",background:T.bg,padding:"14px 14px 32px"}}>
      <SectionHeader title="Time off requests"/>
      {role==="family"&&pendingTimeOff.length>0&&<div style={{background:AB,borderRadius:10,padding:"10px 14px",marginBottom:12,borderLeft:`3px solid ${AM}`}}><div style={{fontSize:13,color:AD,fontWeight:600}}>{pendingTimeOff.length} request{pendingTimeOff.length>1?"s":""} awaiting approval</div></div>}
      {role==="caregiver"&&<button onClick={()=>setShowTimeOffModal(true)} style={{width:"100%",padding:13,borderRadius:12,background:`linear-gradient(135deg,${SF},${SFD})`,color:"#fff",border:"none",cursor:"pointer",fontSize:14,fontWeight:700,marginBottom:16}}>Request time off</button>}
      {timeOffRequests.filter(r=>r.family===activeFamilyId).length===0&&<div style={{textAlign:"center",padding:"32px 16px",color:T.light,fontSize:13}}>No time off requests yet.</div>}
      {timeOffRequests.filter(r=>r.family===activeFamilyId).map(req=>(
        <div key={req.id} style={{background:T.card,borderRadius:14,border:"0.5px solid "+req.status==="pending"?AM:req.status==="approved"?SF:T.border,padding:"14px 16px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:req.status==="pending"&&role==="family"?8:0}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:T.text}}>{req.type}{req.partial?" — Partial day":""}</div>
              <div style={{fontSize:12,color:T.light,marginTop:2}}>{req.date}{req.endDate&&req.endDate!==req.date?` – ${req.endDate}`:""}{req.partial&&` · Leaving ${req.departureTime}${req.noReturn?", not returning":req.returnTime?`, back ${req.returnTime}`:""}`}{req.hours&&` · ~${req.hours} hrs`}</div>
              {req.note&&<div style={{fontSize:12,color:T.mid,marginTop:3,fontStyle:"italic"}}>"{req.note}"</div>}
            </div>
            <span style={{fontSize:11,padding:"3px 10px",borderRadius:20,background:req.status==="pending"?AB:req.status==="approved"?SFB:"#FFEBEE",color:req.status==="pending"?AD:req.status==="approved"?SFD:ERR,fontWeight:700,flexShrink:0,marginLeft:8}}>{req.status==="pending"?"Pending":req.status==="approved"?"Approved":"Declined"}</span>
          </div>
          {role==="family"&&req.status==="pending"&&<div style={{display:"flex",gap:8}}><button onClick={()=>setTimeOffRequests(p=>p.map(r=>r.id===req.id?{...r,status:"declined"}:r))} style={{flex:1,padding:"8px",borderRadius:10,border:"1px solid "+T.border,background:T.card,color:T.mid,cursor:"pointer",fontSize:13}}>Decline</button><button onClick={()=>setTimeOffRequests(p=>p.map(r=>r.id===req.id?{...r,status:"approved"}:r))} style={{flex:2,padding:"8px",borderRadius:10,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Approve</button></div>}
        </div>
      ))}
      {showTimeOffModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={()=>setShowTimeOffModal(false)}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px",maxHeight:"85vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{width:40,height:4,background:T.border,borderRadius:2,margin:"0 auto 16px"}}/>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:16}}>Request time off</div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Type</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{["Vacation","Personal day","Appointment","Bereavement","Other"].map(t=>(<button key={t} onClick={()=>setTimeOffForm(p=>({...p,type:t}))} style={{padding:"6px 12px",borderRadius:20,border:"1.5px solid "+timeOffForm.type===t?SF:T.border,background:timeOffForm.type===t?SFB:T.card,color:timeOffForm.type===t?SFD:T.mid,fontSize:12,cursor:"pointer",fontWeight:timeOffForm.type===t?700:400}}>{t}</button>))}</div>
            </div>
            <div style={{marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:T.bg,borderRadius:10}}>
              <div><div style={{fontSize:13,fontWeight:600,color:T.text}}>Partial day</div><div style={{fontSize:11,color:T.light}}>Leaving early or arriving late</div></div>
              <button onClick={()=>setTimeOffForm(p=>({...p,partial:!p.partial}))} style={{width:44,height:24,borderRadius:12,background:timeOffForm.partial?SF:T.border,border:"none",cursor:"pointer",position:"relative"}}><div style={{width:20,height:20,borderRadius:"50%",background:"#fff",position:"absolute",top:2,left:timeOffForm.partial?22:2}}/></button>
            </div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>{timeOffForm.partial?"Date":"Start date"}</div><input type="date" value={timeOffForm.date} onChange={e=>setTimeOffForm(p=>({...p,date:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
              {!timeOffForm.partial&&<div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>End date</div><input type="date" value={timeOffForm.endDate} onChange={e=>setTimeOffForm(p=>({...p,endDate:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>}
            </div>
            {timeOffForm.partial&&<>
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Leaving at</div><input type="time" value={timeOffForm.departureTime} onChange={e=>setTimeOffForm(p=>({...p,departureTime:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
                {!timeOffForm.noReturn&&<div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Returning at</div><input type="time" value={timeOffForm.returnTime} onChange={e=>setTimeOffForm(p=>({...p,returnTime:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>}
              </div>
              <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:10}}><button onClick={()=>setTimeOffForm(p=>({...p,noReturn:!p.noReturn,returnTime:""}))} style={{width:20,height:20,borderRadius:4,border:"2px solid "+timeOffForm.noReturn?SF:T.border,background:timeOffForm.noReturn?SF:"transparent",cursor:"pointer",flexShrink:0}}/><div style={{fontSize:13,color:T.mid}}>Not returning — end of day</div></div>
              <div style={{marginBottom:12}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Estimated hours affected</div><input type="number" step="0.5" min="0" max="12" value={timeOffForm.hours} onChange={e=>setTimeOffForm(p=>({...p,hours:e.target.value}))} placeholder="e.g. 1.5" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
            </>}
            <div style={{marginBottom:16}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Note (optional)</div><input value={timeOffForm.note} onChange={e=>setTimeOffForm(p=>({...p,note:e.target.value}))} placeholder="e.g. Dentist at 3pm" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
            <div style={{padding:"10px 12px",background:AB,borderRadius:10,marginBottom:16,borderLeft:`3px solid ${AM}`}}><div style={{fontSize:12,color:AD,lineHeight:1.6}}>For same-day illness, contact your family directly by phone. This form is for planned time off only.</div></div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setShowTimeOffModal(false)} style={{flex:1,padding:12,borderRadius:12,border:"1px solid "+T.border,background:T.card,color:T.mid,cursor:"pointer",fontSize:13}}>Cancel</button>
              <button onClick={()=>{if(!timeOffForm.date)return;setTimeOffRequests(p=>[...p,{id:`to${Date.now()}`,...timeOffForm,family:activeFamilyId,created:new Date().toISOString(),status:"pending"}]);setShowTimeOffModal(false);}} style={{flex:2,padding:12,borderRadius:12,background:`linear-gradient(135deg,${SF},${SFD})`,color:"#fff",border:"none",cursor:"pointer",fontSize:13,fontWeight:700}}>Submit request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── HOURS TAB (family) ─────────────────────────────────────────────────────
  const HoursTab=(
    <div style={{flex:1,overflowY:"auto",background:BG,padding:"16px 16px 32px"}}>
      <SectionHeader title="Hours log"/>
      <div style={{background:"#fff",borderRadius:14,border:"0.5px solid "+BD,overflow:"hidden",marginBottom:14}}>
        {clockLog.filter(c=>c.family===activeFamilyId).map((entry,i,arr)=>(
          <div key={entry.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderBottom:i<arr.length-1?`0.5px solid ${BD}`:"none",background:!entry.approved?"#FFFDF8":"#fff"}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600,color:DK}}>{entry.date}</div>
              <div style={{fontSize:11,color:LT}}>{entry.in} → {entry.out} · {entry.hours}h</div>
            </div>
            {entry.approved?(
              <Chip t="✓ Approved" bg={RB} tc={R}/>
            ):(
              <button onClick={()=>setClockLog(p=>p.map(c=>c.id===entry.id?{...c,approved:true}:c))} style={{padding:"5px 14px",borderRadius:20,background:SF,color:"#fff",border:"none",cursor:"pointer",fontSize:12,fontWeight:700}}>Approve</button>
            )}
          </div>
        ))}
      </div>
      <div style={{background:SFB,borderRadius:10,padding:"10px 14px",fontSize:12,color:SFD}}>
        Total approved this week: <strong>{clockLog.filter(c=>c.family===activeFamilyId&&c.approved).reduce((a,c)=>a+c.hours,0).toFixed(2)}h</strong>
      </div>
    </div>
  );

  // ── BOTTOM NAV ─────────────────────────────────────────────────────────────
  const TABS=[
    {id:"home",icon:"⌂",label:"Home"},
    {id:"tracking",icon:"👶",label:"Tracking"},
    {id:"messages",icon:"💬",label:"Messages",badge:msgs.filter(m=>m.from!==(role==="family"?"family":"caregiver")).length},
    {id:"calendar",icon:"📅",label:"Calendar"},
    {id:"more",icon:"···",label:"More"},
  ];
  const MORE_TABS=[
    {id:"todos",icon:"✓",label:"To-do"},
    {id:"incidents",icon:"⚠️",label:"Incidents"},
    {id:"mileage",icon:"🚗",label:"Mileage"},
    {id:"certifications",icon:"🏅",label:"Certs"},
    {id:"emergency",icon:"🚨",label:"Emergency"},
    {id:"access",icon:"🔒",label:"Codes"},
    {id:"documents",icon:"📄",label:"Documents"},
    {id:"reviews",icon:"⭐",label:"Reviews"},
    {id:"absences",icon:"📆",label:"Absences"},
    ...(role==="family"?[{id:"hours",icon:"🕐",label:"Hours"}]:[]),
  ];

  const renderTab=()=>{
    switch(tab){
      case"home":return HomeTab;
      case"tracking":return TrackingTab;
      case"messages":return MessagesTab;
      case"calendar":return CalendarTab;
      case"timeoff":return TimeOffTab;
      case"todos":return TodosTab;
      case"incidents":return IncidentsTab;
      case"mileage":return MileageTab;
      case"certifications":return CertificationsTab;
      case"emergency":return EmergencyTab;
      case"access":return AccessCodesTab;
      case"documents":return DocumentsTab;
      case"reviews":return ReviewsTab;
      case"absences":return AbsencesTab;
      case"hours":return HoursTab;
      default:return HomeTab;
    }
  };

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      if(session&&session.user&&session.user.email_confirmed_at){
        setUser(session.user);
        setRole(session.user.user_metadata?.role||"family");
        if(ADMIN_EMAILS.includes(session.user.email)) setIsPro(true);
        setScreen("app");
      } else {
        if(session&&session.user&&!session.user.email_confirmed_at){
          supabase.auth.signOut();
        }
        setScreen("welcome");
      }
    });
    const{data:{subscription}}=supabase.auth.onAuthStateChange((_event,session)=>{
      if(session){setUser(session.user);}
      else{setUser(null);setScreen("welcome");}
    });
    return()=>subscription.unsubscribe();
  },[]);

  const handleSignup=async(selectedRole)=>{
    if(!authEmail||!authPassword){setAuthError("Please enter your email and a password.");return;}
    if(authPassword.length<8){setAuthError("Password must be at least 8 characters.");return;}
    setAuthLoading(true);setAuthError("");
    const{error}=await supabase.auth.signUp({
      email:authEmail,password:authPassword,
      options:{data:{role:selectedRole}}
    });
    setAuthLoading(false);
    if(error){setAuthError(error.message);}
    else{setShowConfirmMsg(true);}
  };

  const handleLogin=async()=>{
    if(!authEmail||!authPassword){setAuthError("Please enter your email and password.");return;}
    setAuthLoading(true);setAuthError("");
    const{data,error}=await supabase.auth.signInWithPassword({email:authEmail,password:authPassword});
    setAuthLoading(false);
    if(error){setAuthError(error.message);return;}
    setUser(data.user);
    setRole(data.user.user_metadata?.role||"family");
    if(ADMIN_EMAILS.includes(data.user.email)) setIsPro(true);
    setScreen("app");
  };

  const handleSignout=async()=>{
    await supabase.auth.signOut();
    setUser(null);setScreen("welcome");
    setAuthEmail("");setAuthPassword("");setAuthError("");
  };

  // Live weather fetch
  useEffect(()=>{
    const fetchWeather=async(lat,lon)=>{
      setWeatherLoading(true);
      try{
        const res=await fetch("https://api.openweathermap.org/data/2.5/weather?lat="+lat+"&lon="+lon+"&appid="+WEATHER_API_KEY+"&units=imperial");
        const data=await res.json();
        if(data.main&&data.weather){
          const temp=Math.round(data.main.temp);
          const cond=data.weather[0].main.toLowerCase();
          let condition="sunny_warm";
          if(cond.includes("snow")) condition="snowy";
          else if(cond.includes("rain")||cond.includes("drizzle")||cond.includes("thunder")) condition="rainy";
          else if(cond.includes("cloud")||cond.includes("overcast")) condition=temp>55?"cloudy":"sunny_cool";
          else if(temp<50) condition="sunny_cool";
          setWeather({condition,temp,desc:data.weather[0].description+" in "+data.name});
        }
      }catch(e){console.log("Weather error",e);}
      setWeatherLoading(false);
    };
    if(navigator.geolocation){
      navigator.geolocation.getCurrentPosition(
        pos=>fetchWeather(pos.coords.latitude,pos.coords.longitude),
        ()=>setWeatherLoading(false)
      );
    }
  },[]);

  if(screen==="welcome") return(
    <div style={WRAP}><div style={SHELL}>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"28px 24px 48px",background:BG}}>
        <div style={{marginBottom:32,textAlign:"center"}}>
          <div style={{fontSize:34,fontWeight:800,color:DK,letterSpacing:"-0.5px",marginBottom:8}}>Al<span style={{color:SF}}>dear</span>a</div>
          <div style={{fontSize:13,fontWeight:600,color:SF,letterSpacing:"0.04em",textTransform:"uppercase",marginBottom:12}}>Home</div>
          <div style={{width:32,height:2,background:BD,borderRadius:2,margin:"0 auto 16px"}}/>
          <div style={{fontSize:14,color:MD,lineHeight:1.8,maxWidth:280}}>A shared workspace for you and your caregiver.</div>
        </div>
        <div style={{width:"100%",maxWidth:340,display:"flex",flexDirection:"column",gap:12}}>
          <button onClick={()=>{setRole("family");setScreen("auth-signup");}}
            style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"20px",borderRadius:14,border:"1.5px solid "+BD,background:"#fff",cursor:"pointer",textAlign:"left"}}>
            <div style={{width:44,height:44,borderRadius:10,background:RB,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <div style={{width:18,height:18,borderRadius:3,background:R,opacity:0.8}}/>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:15,fontWeight:700,color:DK,marginBottom:3}}>Set up our family workspace</div>
              <div style={{fontSize:12,color:MD,lineHeight:1.5}}>Create your account and invite your caregiver to connect</div>
            </div>
          </button>
          <div style={{padding:"14px 16px",background:SFB,borderRadius:12,borderLeft:`3px solid ${SF}`}}>
            <div style={{fontSize:12,color:SFD,lineHeight:1.7}}>Your caregiver joins through an invite link — they do not create an account independently.</div>
          </div>
          <div style={{padding:"14px 16px",background:"#F5F0E8",borderRadius:12,borderLeft:`3px solid rgba(30,70,50,0.10)`}}>
            <div style={{fontSize:12,color:MD,lineHeight:1.7}}>Looking for a caregiver? <span style={{fontWeight:700,color:R,cursor:"pointer"}} onClick={()=>window.open("https://aldeara.com","_blank")}>Find one on Aldeara Match →</span></div>
          </div>
          <button onClick={()=>setScreen("auth-login")} style={{width:"100%",padding:"14px 20px",borderRadius:14,border:"1.5px solid "+BD,background:"#fff",color:DK,cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"center"}}>Sign in to existing account</button>
        </div>
      </div>
    </div></div>
  );


  if(screen==="app") return(
    <div style={{...WRAP,background:dark?"#0A1410":"#E8E3D8"}}><div style={{...SHELL,background:T.bg}}>
      {TopBar}
      {FamilySelector}
      {renderTab()}

      {/* Signature canvas */}
      {showSigCanvas&&(
        <SignatureCanvas
          onSave={(sig)=>{setDocs(p=>p.map(d=>d.id===sigTargetDoc?{...d,signed:true,sig}:d));setShowSigCanvas(false);setSigTargetDoc(null);}}
          onCancel={()=>{setShowSigCanvas(false);setSigTargetDoc(null);}}
        />
      )}

      {/* More drawer */}
      {showMore&&(
        <div style={{position:"fixed",inset:0,background:T.overlay,zIndex:40,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={()=>setShowMore(false)}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 80px"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:13,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:14}}>More features</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
              {MORE_TABS.map(({id,icon,label})=>(
                <button key={id} onClick={()=>{setTab(id);setShowMore(false);}} style={{padding:"12px 8px",borderRadius:12,border:"0.5px solid "+T.border,background:tab===id?T.sfb:T.card,cursor:"pointer",textAlign:"center"}}>
                  <div style={{fontSize:20,marginBottom:4}}>{icon}</div>
                  <div style={{fontSize:11,color:tab===id?T.sfd:T.mid,fontWeight:tab===id?700:400,lineHeight:1.3}}>{label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      
      {/* Tracking detail modals */}
      {showTrackModal&&showTrackModal!=="select"&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:50,display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={()=>setShowTrackModal(false)}>
          <div style={{width:"100%",maxWidth:430,background:T.card,borderRadius:"20px 20px 0 0",padding:"20px 20px 48px",maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{width:40,height:4,background:T.border,borderRadius:2,margin:"0 auto 16px"}}/>
            <div style={{fontSize:15,fontWeight:700,color:T.text,marginBottom:16}}>
              {showTrackModal==="bottle"?"Log bottle":showTrackModal==="meal"?"Log meal":showTrackModal==="diaper"?"Log diaper":showTrackModal==="sleep"?"Log sleep":showTrackModal==="nursing"?"Log nursing":showTrackModal==="tummy"?"Log tummy time":showTrackModal==="adventure"?"Log adventure":showTrackModal==="medicine"?"Log medicine":showTrackModal==="toileting"?"Log toilet":showTrackModal==="incident"?"Log incident":"Log entry"}
            </div>

            {/* Time field - always shown */}
            <div style={{marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Time</div>
              <input type="time" value={trackForm.logtime} onChange={e=>setTrackForm(p=>({...p,logtime:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/>
            </div>

            {/* Bottle */}
            {showTrackModal==="bottle"&&<>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Type</div>
                <div style={{display:"flex",gap:8}}>{["Breastmilk","Formula","Whole milk","Other"].map(t=><button key={t} onClick={()=>setTrackForm(p=>({...p,bttype:t}))} style={{flex:1,padding:"8px 4px",borderRadius:10,border:"1.5px solid "+(trackForm.bttype===t?SF:T.border),background:trackForm.bttype===t?SFB:T.card,color:trackForm.bttype===t?SFD:T.mid,fontSize:11,cursor:"pointer",fontWeight:trackForm.bttype===t?700:400}}>{t}</button>)}</div>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:12}}>
                <div style={{flex:2}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Amount</div><input type="number" step="0.5" min="0" value={trackForm.btamt} onChange={e=>setTrackForm(p=>({...p,btamt:e.target.value}))} placeholder="0" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
                <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Unit</div><select value={trackForm.btunit} onChange={e=>setTrackForm(p=>({...p,btunit:e.target.value}))} style={{width:"100%",padding:"10px 8px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}><option value="oz">oz</option><option value="ml">ml</option></select></div>
              </div>
            </>}

            {/* Meal */}
            {showTrackModal==="meal"&&<>
              <div style={{marginBottom:12}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Food</div><input value={trackForm.mlfood} onChange={e=>setTrackForm(p=>({...p,mlfood:e.target.value}))} placeholder="e.g. Banana, oatmeal, yogurt" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
              <div style={{marginBottom:12}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Amount (optional)</div><input value={trackForm.mlamt} onChange={e=>setTrackForm(p=>({...p,mlamt:e.target.value}))} placeholder="e.g. Half portion, 3 bites" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
            </>}

            {/* Diaper */}
            {showTrackModal==="diaper"&&<>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Type</div>
                <div style={{display:"flex",gap:8}}>{["Wet","Dirty","Both","Dry"].map(t=><button key={t} onClick={()=>setTrackForm(p=>({...p,dptype:t.toLowerCase()}))} style={{flex:1,padding:"10px 4px",borderRadius:10,border:"1.5px solid "+(trackForm.dptype===t.toLowerCase()?SF:T.border),background:trackForm.dptype===t.toLowerCase()?SFB:T.card,color:trackForm.dptype===t.toLowerCase()?SFD:T.mid,fontSize:12,cursor:"pointer",fontWeight:trackForm.dptype===t.toLowerCase()?700:400}}>{t}</button>)}</div>
              </div>
            </>}

            {/* Sleep */}
            {showTrackModal==="sleep"&&<>
              <div style={{display:"flex",gap:8,marginBottom:12}}>
                <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Wake time</div><input type="time" value={trackForm.slend} onChange={e=>setTrackForm(p=>({...p,slend:e.target.value}))} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
                <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Quality</div><select value={trackForm.slquality} onChange={e=>setTrackForm(p=>({...p,slquality:e.target.value}))} style={{width:"100%",padding:"10px 8px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}><option value="great">Great</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option></select></div>
              </div>
            </>}

            {/* Nursing */}
            {showTrackModal==="nursing"&&role==="family"&&<>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Side</div>
                <div style={{display:"flex",gap:8}}>{["Left","Right","Both"].map(s=><button key={s} onClick={()=>setTrackForm(p=>({...p,nrside:s.toLowerCase()}))} style={{flex:1,padding:"10px",borderRadius:10,border:"1.5px solid "+(trackForm.nrside===s.toLowerCase()?SF:T.border),background:trackForm.nrside===s.toLowerCase()?SFB:T.card,color:trackForm.nrside===s.toLowerCase()?SFD:T.mid,fontSize:13,cursor:"pointer",fontWeight:trackForm.nrside===s.toLowerCase()?700:400}}>{s}</button>)}</div>
              </div>
              <div style={{marginBottom:12}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Duration (min)</div><input type="number" min="0" value={trackForm.nrtimerdur} onChange={e=>setTrackForm(p=>({...p,nrtimerdur:e.target.value}))} placeholder="0" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
            </>}

            {/* Tummy time */}
            {showTrackModal==="tummy"&&<>
              <div style={{marginBottom:12}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Duration (min)</div><input type="number" min="0" value={trackForm.ttdur} onChange={e=>setTrackForm(p=>({...p,ttdur:e.target.value}))} placeholder="0" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
            </>}

            {/* Adventure */}
            {showTrackModal==="adventure"&&<>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Destination</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>{["Playground","Library","Farm","Park","Beach","Woods","Museum","Other"].map(d=><button key={d} onClick={()=>setTrackForm(p=>({...p,avdest:d}))} style={{padding:"5px 12px",borderRadius:20,border:"1.5px solid "+(trackForm.avdest===d?SF:T.border),background:trackForm.avdest===d?SFB:T.card,color:trackForm.avdest===d?SFD:T.mid,fontSize:12,cursor:"pointer",fontWeight:trackForm.avdest===d?700:400}}>{d}</button>)}</div>
              </div>
              <div style={{marginBottom:12}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Note (optional)</div><input value={trackForm.avnote} onChange={e=>setTrackForm(p=>({...p,avnote:e.target.value}))} placeholder="e.g. Fed the ducks, found a caterpillar" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
            </>}

            {/* Toilet */}
            {showTrackModal==="toileting"&&<>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Type</div>
                <div style={{display:"flex",gap:8}}>{["Pee","Poo","Both","Accident"].map(t=><button key={t} onClick={()=>setTrackForm(p=>({...p,totype:t.toLowerCase()}))} style={{flex:1,padding:"10px 4px",borderRadius:10,border:"1.5px solid "+(trackForm.totype===t.toLowerCase()?SF:T.border),background:trackForm.totype===t.toLowerCase()?SFB:T.card,color:trackForm.totype===t.toLowerCase()?SFD:T.mid,fontSize:12,cursor:"pointer",fontWeight:trackForm.totype===t.toLowerCase()?700:400}}>{t}</button>)}</div>
              </div>
            </>}

            {/* Medicine */}
            {showTrackModal==="medicine"&&<>
              <div style={{marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Quick select</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>{["Tylenol","Motrin","Benadryl","Teething gel","Gripe water","Saline"].map(m=><button key={m} onClick={()=>setTrackForm(p=>({...p,mdpreset:m,mdname:""}))} style={{padding:"5px 10px",borderRadius:20,border:"1.5px solid "+(trackForm.mdpreset===m?SF:T.border),background:trackForm.mdpreset===m?SFB:T.card,color:trackForm.mdpreset===m?SFD:T.mid,fontSize:11,cursor:"pointer",fontWeight:trackForm.mdpreset===m?700:400}}>{m}</button>)}</div>
                <input value={trackForm.mdname} onChange={e=>setTrackForm(p=>({...p,mdname:e.target.value,mdpreset:""}))} placeholder="Or type medication name (e.g. Amoxicillin)" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/>
              </div>
              <div style={{display:"flex",gap:8,marginBottom:10}}>
                <div style={{flex:2}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Dose</div><input type="number" step="0.5" min="0" value={trackForm.mddose} onChange={e=>setTrackForm(p=>({...p,mddose:e.target.value}))} placeholder="0" style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}/></div>
                <div style={{flex:1}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>Unit</div><select value={trackForm.mdunit} onChange={e=>setTrackForm(p=>({...p,mdunit:e.target.value}))} style={{width:"100%",padding:"10px 8px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,boxSizing:"border-box"}}><option value="ml">ml</option><option value="mg">mg</option><option value="tsp">tsp</option><option value="tablet">tablet</option></select></div>
              </div>
              <div style={{marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:6}}>Reason</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{["Fever","Teething","Pain","Congestion","Allergies","As prescribed"].map(r=><button key={r} onClick={()=>setTrackForm(p=>({...p,mdreason:r}))} style={{padding:"5px 10px",borderRadius:20,border:"1.5px solid "+(trackForm.mdreason===r?R:T.border),background:trackForm.mdreason===r?RB:T.card,color:trackForm.mdreason===r?RD:T.mid,fontSize:11,cursor:"pointer",fontWeight:trackForm.mdreason===r?700:400}}>{r}</button>)}</div>
              </div>
            </>}

            {/* Incident */}
            {showTrackModal==="incident"&&<>
              <div style={{marginBottom:12}}><div style={{fontSize:11,fontWeight:700,color:T.light,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:4}}>What happened</div><textarea value={trackForm.avnote} onChange={e=>setTrackForm(p=>({...p,avnote:e.target.value}))} placeholder="Describe the incident briefly" rows={3} style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1.5px solid "+T.border,fontSize:13,outline:"none",background:T.card,color:T.text,fontFamily:"inherit",resize:"none",boxSizing:"border-box"}}/></div>
            </>}

            {/* Save button */}
            <button onClick={()=>{
              const cid=selectedChild?.id||"c1";
              const logtime=trackForm.logtime||now();
              const base={id:"e"+Date.now(),time:logtime,childId:cid};
              if(showTrackModal==="bottle") setTracking(p=>({...p,[cid]:{...p[cid],bottles:[base,{...base,amt:trackForm.btamt,unit:trackForm.btunit,type:trackForm.bttype},...(p[cid]?.bottles||[])]}}));
              else if(showTrackModal==="meal") setTracking(p=>({...p,[cid]:{...p[cid],meals:[{...base,food:trackForm.mlfood,amt:trackForm.mlamt},...(p[cid]?.meals||[])]}}));
              else if(showTrackModal==="diaper") setTracking(p=>({...p,[cid]:{...p[cid],diapers:[{...base,type:trackForm.dptype},...(p[cid]?.diapers||[])]}}));
              else if(showTrackModal==="sleep") setTracking(p=>({...p,[cid]:{...p[cid],sleeps:[{...base,start:logtime,end:trackForm.slend,quality:trackForm.slquality},...(p[cid]?.sleeps||[])]}}));
              else if(showTrackModal==="nursing") setTracking(p=>({...p,[cid]:{...p[cid],nursing:[{...base,side:trackForm.nrside,duration:trackForm.nrtimerdur},...(p[cid]?.nursing||[])]}}));
              else if(showTrackModal==="tummy") setTracking(p=>({...p,[cid]:{...p[cid],tummy:[{...base,dur:trackForm.ttdur},...(p[cid]?.tummy||[])]}}));
              else if(showTrackModal==="adventure") setTracking(p=>({...p,[cid]:{...p[cid],adventures:[{...base,dest:trackForm.avdest,note:trackForm.avnote},...(p[cid]?.adventures||[])]}}));
              else if(showTrackModal==="toileting") setTracking(p=>({...p,[cid]:{...p[cid],toileting:[{...base,type:trackForm.totype},...(p[cid]?.toileting||[])]}}));
              else if(showTrackModal==="medicine"){
                const med=trackForm.mdpreset||trackForm.mdname;
                const intervals={"Tylenol":4,"Motrin":6,"Benadryl":6};
                const hrs=intervals[med];
                let nextDose=null;
                if(hrs&&trackForm.logtime){const[h,m]=trackForm.logtime.split(":").map(Number);const nd=new Date();nd.setHours(h+hrs,m,0,0);nextDose=nd.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});}
                setTracking(p=>({...p,[cid]:{...p[cid],medicines:[{...base,name:med||"Medicine",dose:trackForm.mddose,unit:trackForm.mdunit,reason:trackForm.mdreason,nextDose},...(p[cid]?.medicines||[])]}}));
              }
              else if(showTrackModal==="incident") setTracking(p=>({...p,[cid]:{...p[cid],incidents:[{...base,note:trackForm.avnote},...(p[cid]?.incidents||[])]}}));
              setShowTrackModal(false);
            }} style={{width:"100%",padding:13,borderRadius:12,background:"linear-gradient(135deg,"+SF+","+SFD+")",color:"#fff",border:"none",cursor:"pointer",fontSize:14,fontWeight:700,marginTop:8}}>Save</button>
          </div>
        </div>
      )}

{/* Bottom nav */}
      <div style={{display:"flex",borderTop:`0.5px solid ${T.border}`,background:T.bar,flexShrink:0}}>
        {TABS.map(({id,icon,label,badge})=>{
          const isMoreActive=MORE_TABS.map(t=>t.id).includes(tab)&&id==="more";
          const isActive=tab===id||isMoreActive;
          return(
            <button key={id} onClick={()=>{if(id==="more"){setShowMore(s=>!s);}else{setTab(id);setShowMore(false);}}}
              style={{flex:1,padding:"10px 0",background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,color:isActive?SF:T.light,position:"relative"}}>
              <span style={{fontSize:18}}>{icon}</span>
              <span style={{fontSize:10,fontWeight:isActive?700:400}}>{label}</span>
              {badge>0&&<div style={{position:"absolute",top:6,right:"calc(50% - 18px)",width:8,height:8,borderRadius:"50%",background:SF}}/>}
            </button>
          );
        })}
      </div>
    </div></div>
  );

  if(screen==="auth-signup") return(
    <div style={WRAP}><div style={SHELL}>
      <div style={{padding:"14px 20px 12px",display:"flex",alignItems:"center",gap:12,borderBottom:"0.5px solid "+BD,background:BG,flexShrink:0}}>
        <button onClick={()=>setScreen("welcome")} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:MD}}>&#8592;</button>
        <div style={{fontSize:15,fontWeight:700,color:DK}}>Create your account</div>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 24px"}}>
        <div style={{width:"100%",maxWidth:340}}>
          {showConfirmMsg?(
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:13,color:MD,marginBottom:16,lineHeight:1.7}}>We sent a confirmation link to {authEmail}. Click it to activate your account then come back to sign in.</div>
              <button onClick={()=>setScreen("auth-login")} style={{width:"100%",padding:14,borderRadius:14,background:"linear-gradient(135deg,"+SF+","+SFD+")",color:"#fff",border:"none",cursor:"pointer",fontSize:14,fontWeight:700}}>Go to sign in</button>
            </div>
          ):(
            <>
              <div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.04em",display:"block",marginBottom:4}}>Email</label><input type="email" value={authEmail} onChange={e=>{setAuthEmail(e.target.value);setAuthError("");}} placeholder="your@email.com" style={{width:"100%",padding:"11px 13px",borderRadius:11,border:"1.5px solid "+BD,fontSize:14,outline:"none",background:"#fff",boxSizing:"border-box"}}/></div>
              <div style={{marginBottom:8}}><label style={{fontSize:11,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.04em",display:"block",marginBottom:4}}>Password</label><input type="password" value={authPassword} onChange={e=>{setAuthPassword(e.target.value);setAuthError("");}} placeholder="At least 8 characters" style={{width:"100%",padding:"11px 13px",borderRadius:11,border:"1.5px solid "+BD,fontSize:14,outline:"none",background:"#fff",boxSizing:"border-box"}}/></div>
              {authError&&<div style={{padding:"10px 12px",background:"#FFEBEE",borderRadius:10,marginBottom:10,borderLeft:"3px solid #E53935"}}><div style={{fontSize:12,color:"#E53935"}}>{authError}</div></div>}
              <div style={{padding:"10px 12px",background:SFB,borderRadius:10,marginBottom:14,borderLeft:"3px solid "+SF}}><div style={{fontSize:11,color:SFD,lineHeight:1.6}}>Already use an Aldeara product? Sign in with the same email and password.</div></div>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.04em",marginBottom:8}}>I am joining as</div>
                <div style={{display:"flex",gap:8}}>
                  {[{v:"family",l:"A family"},{v:"caregiver",l:"A caregiver"}].map(o=>(
                    <button key={o.v} onClick={()=>setRole(o.v)} style={{flex:1,padding:"10px",borderRadius:11,border:"1.5px solid "+(role===o.v?SF:BD),background:role===o.v?SFB:"#fff",color:role===o.v?SFD:"#4A6558",fontSize:13,cursor:"pointer",fontWeight:role===o.v?700:400}}>{o.l}</button>
                  ))}
                </div>
              </div>
              <button onClick={()=>handleSignup(role)} disabled={authLoading} style={{width:"100%",padding:14,borderRadius:14,background:"linear-gradient(135deg,"+SF+","+SFD+")",color:"#fff",border:"none",cursor:"pointer",fontSize:15,fontWeight:700,opacity:authLoading?0.7:1}}>{authLoading?"Creating account...":"Create account"}</button>
              <div style={{textAlign:"center",marginTop:16,fontSize:12,color:LT}}>Already have an account? <span onClick={()=>{setAuthError("");setScreen("auth-login");}} style={{color:SF,fontWeight:600,cursor:"pointer"}}>Sign in</span></div>
            </>
          )}
        </div>
      </div>
    </div></div>
  );

  if(screen==="auth-login") return(
    <div style={WRAP}><div style={SHELL}>
      <div style={{padding:"14px 20px 12px",display:"flex",alignItems:"center",gap:12,borderBottom:"0.5px solid "+BD,background:BG,flexShrink:0}}>
        <button onClick={()=>setScreen("welcome")} style={{background:"none",border:"none",cursor:"pointer",fontSize:22,color:MD}}>&#8592;</button>
        <div style={{fontSize:15,fontWeight:700,color:DK}}>Sign in</div>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 24px"}}>
        <div style={{width:"100%",maxWidth:340}}>
          <div style={{marginBottom:14}}><label style={{fontSize:11,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.04em",display:"block",marginBottom:4}}>Email</label><input type="email" value={authEmail} onChange={e=>{setAuthEmail(e.target.value);setAuthError("");}} placeholder="your@email.com" style={{width:"100%",padding:"11px 13px",borderRadius:11,border:"1.5px solid "+BD,fontSize:14,outline:"none",background:"#fff",boxSizing:"border-box"}}/></div>
          <div style={{marginBottom:8}}><label style={{fontSize:11,fontWeight:700,color:LT,textTransform:"uppercase",letterSpacing:"0.04em",display:"block",marginBottom:4}}>Password</label><input type="password" value={authPassword} onChange={e=>{setAuthPassword(e.target.value);setAuthError("");}} placeholder="Password" style={{width:"100%",padding:"11px 13px",borderRadius:11,border:"1.5px solid "+BD,fontSize:14,outline:"none",background:"#fff",boxSizing:"border-box"}}/></div>
          {authError&&<div style={{padding:"10px 12px",background:"#FFEBEE",borderRadius:10,marginBottom:10,borderLeft:"3px solid #E53935"}}><div style={{fontSize:12,color:"#E53935"}}>{authError}</div></div>}
          <div style={{padding:"10px 12px",background:SFB,borderRadius:10,marginBottom:14,borderLeft:"3px solid "+SF}}><div style={{fontSize:11,color:SFD,lineHeight:1.6}}>Already use an Aldeara product? Sign in with the same email and password.</div></div>
          <button onClick={handleLogin} disabled={authLoading} style={{width:"100%",padding:14,borderRadius:14,background:"linear-gradient(135deg,"+SF+","+SFD+")",color:"#fff",border:"none",cursor:"pointer",fontSize:15,fontWeight:700,opacity:authLoading?0.7:1}}>{authLoading?"Signing in...":"Sign in"}</button>
          <div style={{textAlign:"center",marginTop:16,fontSize:12,color:LT}}>No account yet? <span onClick={()=>{setAuthError("");setScreen("auth-signup");}} style={{color:SF,fontWeight:600,cursor:"pointer"}}>Create one free</span></div>
          <div style={{textAlign:"center",marginTop:8}}><span onClick={async()=>{if(!authEmail){setAuthError("Enter your email above first.");return;}await supabase.auth.resetPasswordForEmail(authEmail);alert("Password reset link sent to "+authEmail);}} style={{fontSize:12,color:LT,cursor:"pointer",textDecoration:"underline"}}>Forgot password?</span></div>
        </div>
      </div>
    </div></div>
  );

  return null;
}
